/*!
 * EGT Platform section builders — surface-specific UA/JSON.
 *
 * Composes with the shared egt.sections.js + the shared _egt.scss tokens/canvas/
 * glass/motion. Platform-only styling lives in app/scss/egt.platform.scss; the
 * Lens / make-it-yours / vignette behavior lives in app/public/src/egt.platform.js
 * (referenced AFTER the shared egt.home.js client). Pure builders — no behavior.
 *
 * IP firewall: WHY + WHAT, never HOW. Every vignette is an illustrative,
 * sample-data recreation in the platform's frosted-glass language — it is NOT a
 * live platform call and reveals nothing reconstructable.
 */
var S = require("../helpers/egt.sections.js");

/* ---- the six lenses (single source of truth) ----------------------- */
var LENSES = [
  { id: "site", label: "Site", href: "/platform/site",
    copy: "A presence you own — pages, profiles, and media, brand-aware and fast.",
    img: "/vis/identities.jpg", imgW: "1920", imgH: "1080" },
  { id: "marketplace", label: "Marketplace", href: "/platform/marketplace",
    copy: "Digital goods, physical subscriptions, and more — one universal checkout.",
    img: "/vis/database.jpg", imgW: "1920", imgH: "1920" },
  { id: "reach", label: "Reach", href: "/platform/reach",
    copy: "The message lives beside the relationship — newsletters from the same customer graph.",
    img: "/vis/getthoseemails.jpg", imgW: "1920", imgH: "1920" },
  { id: "engage", label: "Engage", href: "/platform/engage",
    copy: "Loyalty your customers won't resent — opt-in, honest, never a nag.",
    img: "/imgs/egt-platform-engage.jpg", imgW: "1920", imgH: "1280" },
  { id: "appartisan", label: "AppArtisan", href: "/platform/appartisan",
    copy: "Extend, develop, design, deploy — reviewed plugins that grow what you can do.",
    img: "/imgs/egt-platform-nexus.jpg", imgW: "1920", imgH: "2905" },
  { id: "workflows", label: "Workflows", href: "/platform/workflows",
    copy: "When something happens, the follow-up runs itself.",
    img: "/vis/uatools-anim.png", imgW: "1920", imgH: "1920" }
];
exports.LENSES = LENSES;

/* =====================================================================
 * HERO — poster-first media, one frosted card, ambient kenburns.
 * ===================================================================== */
exports.hero = function hero(opts) {
  var inner = [ { n: "p", c: "egt__eyebrow egt-reveal", i: opts.eyebrow } ];
  inner.push({ n: "h1", c: "egt-phero__title egt-reveal", i: opts.title });
  if (opts.lead) { inner.push({ n: "p", c: "egt-phero__lead egt-reveal", i: opts.lead }); }
  if (opts.cta) { inner.push({ n: "div", c: "egt-phero__actions egt-reveal", i: opts.cta }); }
  if (opts.note) { inner.push({ n: "p", c: "egt-pf-hero__note egt-reveal", i: opts.note }); }

  var mediaNode = S.media(opts.media || {});
  /* ambient kenburns on the hero media layer only */
  var leaf = mediaNode.i;
  leaf.c = (leaf.c || "") + " egt-kenburns";

  var children = [ mediaNode ];
  /* the hero crossfades its mood with the Category control too (page-wide
   * reskin). Sits above the video poster, below the frosted card. Only the
   * platform hub hero opts in (not the per-capability pages). */
  if (opts.catBackdrop) { children.push(catBackdrop()); }
  children.push({ n: "div", c: "egt__shell egt-canvas__inner",
    i: S.glass({ width: "wide", content: inner, c: "egt-pf-hero__card" }) });

  return {
    n: "section", c: "egt-pf-hero egt-canvas egt-canvas--center" + (opts.tall === false ? "" : " egt-canvas--tall"),
    i: children
  };
};

/* =====================================================================
 * THE LENS — the signature interactive vignette.
 * One sample object, re-composed through six surfaces. Real HTML for every
 * end-state (crawlable + works with JS off); the client morphs between them.
 * Built as a tablist for keyboard + reduced-motion (instant swap) support.
 * ===================================================================== */
function lensTab(l, i) {
  return {
    n: "button", c: "egt-lens__tab" + (i === 0 ? " is-on" : ""), type: "button",
    role: "tab", id: "egt-lens-tab-" + l.id, "aria-controls": "egt-lens-panel-" + l.id,
    "aria-selected": i === 0 ? "true" : "false", tabindex: i === 0 ? "0" : "-1",
    "data-lens": l.id,
    i: { n: "span", c: "egt-lens__tab-label", i: l.label }
  };
}

/* the sample object, rendered into each surface ---------------------- */
/* SITE — a small partial-website mockup inside the preview frame: a top nav
 * bar (logo + links + a working mobile-menu toggle), a slice of page content
 * (a hero block with a heading + line + product chip), and a footer. HOVER
 * states work; nothing navigates (real <button>s + non-link spans). The
 * hamburger toggles a frosted mobile menu open/closed (initSitePreview). */
var SITE_NAV = [ "Shop", "Story", "Journal", "Contact" ];
function siteView() {
  return [
    { n: "div", c: "egt-lens-site", "data-lens-site": "true",
      i: [
        /* ---- the mock browser top bar ---- */
        { n: "div", c: "egt-lens-site__bar", "aria-hidden": "true",
          i: [ { n: "span", c: "egt-lens-site__dot" }, { n: "span", c: "egt-lens-site__dot" },
            { n: "span", c: "egt-lens-site__dot" },
            { n: "span", c: "egt-lens-site__addr", i: "ateliervesper.studio" } ] },

        /* ---- the partial website ---- */
        { n: "div", c: "egt-lens-site__page",
          i: [
            /* nav bar: logo + desktop links + hamburger */
            { n: "div", c: "egt-lens-site__nav",
              i: [
                { n: "span", c: "egt-lens-site__logo",
                  i: [ { n: "span", c: "egt-lens-site__logo-mk", "aria-hidden": "true", i: "A" },
                    { n: "span", c: "egt-lens-site__logo-tx", i: "Atelier Vesper" } ] },
                { n: "nav", c: "egt-lens-site__links", "aria-label": "Sample site navigation (demo)",
                  i: SITE_NAV.map(function (lbl, n) {
                    return { n: "button", type: "button", c: "egt-lens-site__lk" + (n === 0 ? " is-on" : ""),
                      tabindex: "-1", i: lbl };
                  }) },
                { n: "button", type: "button", c: "egt-lens-site__burger", "data-lens-site-toggle": "true",
                  "aria-expanded": "false", "aria-controls": "egt-lens-site-menu", "aria-label": "Open menu", tabindex: "-1",
                  i: [ { n: "span", c: "egt-lens-site__burger-ln", "aria-hidden": "true" },
                    { n: "span", c: "egt-lens-site__burger-ln", "aria-hidden": "true" },
                    { n: "span", c: "egt-lens-site__burger-ln", "aria-hidden": "true" } ] }
              ] },

            /* the mobile menu (revealed by the hamburger) */
            { n: "div", c: "egt-lens-site__menu", id: "egt-lens-site-menu", hidden: "hidden",
              i: SITE_NAV.map(function (lbl, n) {
                return { n: "button", type: "button", c: "egt-lens-site__menu-lk" + (n === 0 ? " is-on" : ""),
                  tabindex: "-1", style: "--egt-i:" + n, i: lbl };
              }) },

            /* a slice of page content: hero block + heading + line + product chip */
            { n: "div", c: "egt-lens-site__hero",
              i: [
                { n: "div", c: "egt-lens-site__art",
                  i: [ { n: "img", c: "egt-lens-site__art-img", src: "/imgs/pf-carafe.jpg",
                      alt: "The Ember Carafe — hand-thrown stoneware", width: "1600", height: "1067",
                      loading: "lazy", decoding: "async" },
                    { n: "span", c: "egt-lens-site__art-tag", i: "Small batch" } ] },
                { n: "div", c: "egt-lens-site__copy",
                  i: [
                    { n: "p", c: "egt-lens-site__eyebrow", i: "New · Stoneware" },
                    { n: "h3", c: "egt-lens-site__h", i: "The Ember Carafe" },
                    { n: "p", c: "egt-lens-site__sub", i: "Hand-thrown, fired in small batches. A page that loads before they blink — and looks like you." },
                    { n: "div", c: "egt-lens-site__row",
                      i: [ { n: "span", c: "egt-lens-site__price", i: "$68" },
                        { n: "button", type: "button", c: "egt-lens-site__cta", tabindex: "-1", i: "Add to cart" } ] }
                  ] }
              ] },

            /* a tiny footer */
            { n: "div", c: "egt-lens-site__foot",
              i: [ { n: "span", c: "egt-lens-site__foot-bn", i: "Atelier Vesper" },
                { n: "span", c: "egt-lens-site__foot-links",
                  i: [ { n: "span", c: "egt-lens-site__foot-lk", i: "Shop" },
                    { n: "span", c: "egt-lens-site__foot-lk", i: "Story" },
                    { n: "span", c: "egt-lens-site__foot-lk", i: "Journal" } ] },
                { n: "span", c: "egt-lens-site__foot-c", i: "© Studio" } ] }
          ] }
      ] },
    { n: "p", c: "egt-lens-card__story", i: "A presence you own — brand-aware and fast. Open the menu; hover the nav. Every surface, your story." }
  ];
}
/* =====================================================================
 * SHARED MARKETPLACE CONTROL (jobs 4.4 / 6.6 / 6.7)
 * ONE product-card + grid + cart-count control, driven by ONE product list.
 * Used by BOTH the standalone Marketplace section (exports.shop — full grid)
 * AND the "Do more." Marketplace lens panel (sellView — a compact subset), so
 * the two areas read as the same real marketplace. Each product carries a TYPE
 * (Subscription / Digital / Physical), a real matched product photo (Unsplash →
 * sharp AVIF+JPG siblings in /imgs), and an add-to-cart button. The client
 * (initMarket) increments a per-area cart-count pill on add. UNDIMMED imagery;
 * the only frosted-glass over media is the small type badge (§4-compliant).
 * ===================================================================== */
var MARKET = [
  { nm: "Studio Membership", type: "Subscription", meta: "Members' studio access & drops",
    price: "$9", unit: "/mo", img: "/imgs/pf-mkt-membership.jpg" },
  { nm: "Kiln Circle", type: "Subscription", meta: "Loyalty tier · early firings",
    price: "$19", unit: "/mo", img: "/imgs/pf-mkt-kiln.jpg" },
  { nm: "Glaze Preset Pack", type: "Digital", meta: "24 studio glazes · instant download",
    price: "$18", img: "/imgs/pf-mkt-glaze.jpg" },
  { nm: "The Maker's Field Guide", type: "Digital", meta: "eBook · PDF + ePub",
    price: "$12", img: "/imgs/pf-mkt-guide.jpg" },
  { nm: "The Ember Carafe", type: "Physical", meta: "Hand-thrown stoneware",
    price: "$68", img: "/imgs/pf-carafe.jpg" },
  { nm: "Nightglass Tumbler", type: "Physical", meta: "Set of two",
    price: "$120", img: "/imgs/pf-mkt-tumbler.jpg" },
  { nm: "Atelier Tote", type: "Physical", meta: "Heavy natural canvas",
    price: "$38", img: "/imgs/pf-mkt-tote.jpg" },
  { nm: "Halcyon LP", type: "Physical", meta: "180g vinyl",
    price: "$24", img: "/imgs/pf-mkt-lp.jpg" }
];

function mktStroke(paths) {
  return { n: "svg", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor",
    "stroke-width": "1.7", "stroke-linecap": "round", "stroke-linejoin": "round",
    "aria-hidden": "true", focusable: "false", i: paths };
}
function marketCard(p, idx) {
  var key = p.type.toLowerCase();
  var priceKids = [ p.price ];
  if (p.unit) { priceKids.push({ n: "span", c: "egt-mkt-card__unit", i: p.unit }); }
  return {
    n: "article", c: "egt-mkt-card egt-lift", style: "--egt-i:" + idx,
    i: [
      { n: "div", c: "egt-mkt-card__media",
        i: [
          { n: "img", c: "egt-mkt-card__img", src: p.img,
            alt: p.nm, loading: "lazy", decoding: "async", width: "1200", height: "900" },
          { n: "span", c: "egt-mkt-card__badge egt-mkt-card__badge--" + key,
            i: [ { n: "span", c: "egt-mkt-card__badge-dot", "aria-hidden": "true" }, p.type ] }
        ] },
      { n: "div", c: "egt-mkt-card__info",
        i: [
          { n: "h3", c: "egt-mkt-card__nm", i: p.nm },
          { n: "p", c: "egt-mkt-card__meta", i: p.meta },
          { n: "p", c: "egt-mkt-card__price", i: priceKids }
        ] },
      { n: "div", c: "egt-mkt-card__foot",
        i: { n: "button", c: "egt-mkt-card__add", type: "button", "data-market-add": "true",
          "aria-label": (key === "subscription" ? "Subscribe to " : "Add ") + p.nm + (key === "subscription" ? "" : " to cart"),
          i: [
            { n: "span", c: "egt-mkt-card__add-ic", "aria-hidden": "true",
              i: mktStroke([ { n: "path", d: "M12 5v14" }, { n: "path", d: "M5 12h14" } ]) },
            { n: "span", c: "egt-mkt-card__add-tx", i: key === "subscription" ? "Subscribe" : "Add to cart" }
          ] } }
    ]
  };
}
/* opts: { compact, reveal (default true), note } */
function marketGrid(products, opts) {
  opts = opts || {};
  var gridCls = "egt-mkt__grid" + (opts.reveal === false ? "" : " egt-reveal");
  return {
    n: "div", c: "egt-mkt" + (opts.compact ? " egt-mkt--compact" : ""), "data-market-scope": "true",
    i: [
      { n: "div", c: "egt-mkt__bar",
        i: [
          { n: "span", c: "egt-mkt__bar-note", i: opts.note || "Subscriptions · digital · physical — one checkout." },
          { n: "span", c: "egt-mkt__cart", "data-market-cart": "true",
            i: [
              { n: "span", c: "egt-mkt__cart-ic", "aria-hidden": "true",
                i: mktStroke([ { n: "path", d: "M5 7h14l-1.1 10.4a2 2 0 0 1-2 1.8H8.1a2 2 0 0 1-2-1.8z" },
                  { n: "path", d: "M9 7a3 3 0 0 1 6 0" } ]) },
              { n: "span", c: "egt-mkt__cart-tx", "aria-live": "polite",
                i: [ "Cart ", { n: "b", "data-market-count": "true", i: "0" } ] }
            ] }
        ] },
      { n: "div", c: gridCls, i: products.map(function (p, i) { return marketCard(p, i); }) }
    ]
  };
}

function sellView() {
  /* compact subset of the SAME control — one of each type + a bestseller */
  var picks = [ MARKET[0], MARKET[2], MARKET[4], MARKET[7] ];
  return [
    marketGrid(picks, { compact: true, reveal: false, note: "One cart · subs, digital & physical." }),
    { n: "p", c: "egt-lens-card__story", i: "Subscriptions, digital downloads and physical goods — variants, inventory and checkout in one flow. Your customer stays yours." }
  ];
}
/* REACH — three little surfaces: orders ready to ship (list + status),
 * a short customer-chat snippet (message bubbles), and an abandoned-cart
 * auto-send email card (subject + a "will send" state). Illustrative. */
var REACH_ORDERS = [
  { id: "#1042", who: "Ember Carafe", st: "Paid", cls: "paid" },
  { id: "#1048", who: "Nightglass set", st: "Packing", cls: "pack" },
  { id: "#1051", who: "Atelier Tote", st: "Ready", cls: "ready" }
];
function reachView() {
  return [
    { n: "div", c: "egt-lens-reach", "data-lens-reach": "true",
      i: [
        /* 1 — orders ready to ship */
        { n: "div", c: "egt-lens-reach__block",
          i: [
            { n: "div", c: "egt-lens-reach__hd",
              i: [ { n: "span", c: "egt-lens-reach__hd-tx", i: "Ready to ship" },
                { n: "span", c: "egt-lens-reach__hd-ct", i: "3" } ] },
            { n: "ul", c: "egt-lens-reach__orders",
              i: REACH_ORDERS.map(function (o, n) {
                return { n: "li", c: "egt-lens-reach__order", style: "--i:" + n,
                  i: [ { n: "span", c: "egt-lens-reach__o-id", i: o.id },
                    { n: "span", c: "egt-lens-reach__o-item", i: o.who },
                    { n: "span", c: "egt-lens-reach__o-st egt-lens-reach__o-st--" + o.cls,
                      i: [ { n: "span", c: "egt-lens-reach__o-dot", "aria-hidden": "true" }, o.st ] } ] };
              }) }
          ] },

        /* 2 — customer chat snippet (messages arrive one-by-one on select) */
        { n: "div", c: "egt-lens-reach__block",
          i: [
            { n: "div", c: "egt-lens-reach__hd",
              i: [ { n: "span", c: "egt-lens-reach__hd-tx", i: "Customer chat" },
                { n: "span", c: "egt-lens-reach__hd-live", i: "Online" } ] },
            { n: "div", c: "egt-lens-reach__chat",
              i: [
                { n: "div", c: "egt-lens-reach__msg egt-lens-reach__msg--them",
                  i: "Is the Ember Carafe dishwasher safe?" },
                { n: "div", c: "egt-lens-reach__typing", "aria-hidden": "true",
                  i: [ { n: "span", c: "egt-lens-reach__typing-dot" },
                    { n: "span", c: "egt-lens-reach__typing-dot" },
                    { n: "span", c: "egt-lens-reach__typing-dot" } ] },
                { n: "div", c: "egt-lens-reach__msg egt-lens-reach__msg--you",
                  i: "It is! Hand-wash keeps the glaze brightest, though." }
              ] }
          ] },

        /* 3 — abandoned-cart auto-send email card (assembles on select) */
        { n: "div", c: "egt-lens-reach__block",
          i: [
            { n: "div", c: "egt-lens-reach__mail",
              i: [
                { n: "div", c: "egt-lens-reach__mail-hd",
                  i: [ { n: "span", c: "egt-lens-reach__mail-tag", i: "Abandoned cart" },
                    { n: "span", c: "egt-lens-reach__mail-state", i: "Auto-send · scheduled" } ] },
                { n: "div", c: "egt-lens-reach__mail-subj", i: "Still thinking it over? Your Ember Carafe is waiting." },
                { n: "div", c: "egt-lens-reach__mail-foot",
                  i: [ { n: "span", c: "egt-lens-reach__mail-when", i: "Will send in 1 hour" },
                    { n: "span", c: "egt-lens-reach__mail-edit", i: "Edit" } ] }
              ] }
          ] }
      ] },
    { n: "p", c: "egt-lens-card__story", i: "The message lives beside the relationship — orders, chats, and follow-ups from one customer graph. You stay in control of every send." }
  ];
}
/* a small inline icon (24-box, currentColor stroke) for the engage cards */
function engageIcon(paths) {
  return { n: "svg", c: "egt-lens-eng__ico-svg", viewBox: "0 0 24 24", fill: "none",
    stroke: "currentColor", "stroke-width": "1.6", "stroke-linecap": "round",
    "stroke-linejoin": "round", "aria-hidden": "true", focusable: "false", i: paths };
}
/* ENGAGE — designed award/loyalty cards: two award "postcards" (each with an
 * inline SVG badge, a reward title, and points/meta) and a loyalty tier card
 * with a progress bar. Gold/currentColor visuals — not just text. */
function engageView() {
  var medal = [ { n: "circle", cx: "12", cy: "9", r: "5" },
    { n: "path", d: "M9 13.5 7.5 21l4.5-2.5 4.5 2.5L15 13.5" } ];
  var spark = [ { n: "path", d: "M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" },
    { n: "circle", cx: "12", cy: "12", r: "2.5" } ];
  return [
    { n: "div", c: "egt-lens-eng", "data-lens-eng": "true",
      i: [
        { n: "div", c: "egt-lens-eng__hd",
          i: [ { n: "span", c: "egt-lens-eng__hd-tx", i: "Atelier Vesper · Members" },
            { n: "span", c: "egt-lens-eng__hd-tag", i: "Opt-in" } ] },

        /* two award postcards */
        { n: "div", c: "egt-lens-eng__cards",
          i: [
            { n: "div", c: "egt-lens-eng__card egt-lens-eng__card--gold",
              i: [
                { n: "span", c: "egt-lens-eng__ico", "aria-hidden": "true", i: engageIcon(medal) },
                { n: "div", c: "egt-lens-eng__body",
                  i: [ { n: "div", c: "egt-lens-eng__title", i: "Second-order thank-you" },
                    { n: "div", c: "egt-lens-eng__meta", i: "A small gift, on the house" } ] },
                { n: "span", c: "egt-lens-eng__pts",
                  i: [ { n: "b", "data-count-to": "150", "data-count-prefix": "+", i: "+150" }, " pts" ] }
              ] },
            { n: "div", c: "egt-lens-eng__card",
              i: [
                { n: "span", c: "egt-lens-eng__ico", "aria-hidden": "true", i: engageIcon(spark) },
                { n: "div", c: "egt-lens-eng__body",
                  i: [ { n: "div", c: "egt-lens-eng__title", i: "Early access to new drops" },
                    { n: "div", c: "egt-lens-eng__meta", i: "See the kiln before anyone" } ] },
                { n: "span", c: "egt-lens-eng__pill", i: "Unlocked" }
              ] }
          ] },

        /* a loyalty tier card with a progress bar */
        { n: "div", c: "egt-lens-eng__tier",
          i: [
            { n: "div", c: "egt-lens-eng__tier-top",
              i: [ { n: "span", c: "egt-lens-eng__tier-nm", i: "Kiln Circle" },
                { n: "span", c: "egt-lens-eng__tier-st", i: "Tier 2 of 3" } ] },
            { n: "div", c: "egt-lens-eng__bar", "aria-hidden": "true",
              i: { n: "span", c: "egt-lens-eng__bar-fill", "data-fill": "64", style: "width:64%" } },
            { n: "div", c: "egt-lens-eng__tier-meta",
              i: [ { n: "span", "data-count-to": "320", "data-count-suffix": " pts", i: "320 pts" },
                { n: "span", i: "180 to Founder" } ] }
          ] }
      ] },
    { n: "p", c: "egt-lens-card__story", i: "Loyalty your customers won't resent — opt-in, transparent, and yours to leave anytime. Appreciation, never a streak about to break." }
  ];
}
/* a small inline glyph (24-box, currentColor) for a plugin tile */
function pluginIcon(paths) {
  return { n: "svg", c: "egt-lens-store__ico-svg", viewBox: "0 0 24 24", fill: "none",
    stroke: "currentColor", "stroke-width": "1.6", "stroke-linecap": "round",
    "stroke-linejoin": "round", "aria-hidden": "true", focusable: "false", i: paths };
}
/* APPARTISAN — a small plugin "app store": a header + a few plugin cards
 * (name, one-line what-it-adds, an Add / Reviewed chip). Conveys an
 * extensible store without revealing HOW anything is built (IP firewall). */
var STORE_PLUGINS = [
  { nm: "Care-guide generator", what: "Drafts a care card for every product",
    chip: "Add", state: "add",
    ico: [ { n: "path", d: "M5 4h10l4 4v12H5z" }, { n: "path", d: "M15 4v4h4" }, { n: "path", d: "M9 13h6M9 16h4" } ] },
  { nm: "Local pickup slots", what: "Adds in-store pickup windows at checkout",
    chip: "✓ Added", state: "added",
    ico: [ { n: "path", d: "M4 8h16l-1.5 11h-13z" }, { n: "path", d: "M8 8a4 4 0 0 1 8 0" } ] },
  { nm: "Studio gift cards", what: "Sell and redeem branded gift cards",
    chip: "Add", state: "add",
    ico: [ { n: "rect", x: "3", y: "6", width: "18", height: "12", rx: "2" }, { n: "path", d: "M3 10h18M7 14h4" } ] }
];
function nexusView() {
  return [
    { n: "div", c: "egt-lens-store",
      i: [
        { n: "div", c: "egt-lens-store__hd",
          i: [ { n: "span", c: "egt-lens-store__hd-tx", i: "Reviewed plugins." },
            { n: "span", c: "egt-lens-store__hd-shield", "aria-hidden": "true",
              i: pluginIcon([ { n: "path", d: "M12 3 5 6v5c0 4 3 6.5 7 8 4-1.5 7-4 7-8V6z" },
                { n: "path", d: "m9 12 2 2 4-4" } ]) } ] },
        { n: "p", c: "egt-lens-store__sub", i: "Every plugin is reviewed before it can touch your business." },
        { n: "ul", c: "egt-lens-store__list",
          i: STORE_PLUGINS.map(function (p) {
            return { n: "li", c: "egt-lens-store__card",
              i: [
                { n: "span", c: "egt-lens-store__ico", "aria-hidden": "true", i: pluginIcon(p.ico) },
                { n: "div", c: "egt-lens-store__body",
                  i: [ { n: "div", c: "egt-lens-store__nm", i: p.nm },
                    { n: "div", c: "egt-lens-store__what", i: p.what } ] },
                { n: "span", c: "egt-lens-store__chip egt-lens-store__chip--" + p.state, i: p.chip }
              ] };
          }) }
      ] },
    { n: "p", c: "egt-lens-card__story", i: "Extend, develop, design, deploy — reviewed plugins that grow what you can do. A door for builders; safety for everyone else." }
  ];
}
/* WORKFLOWS — an interactive 10-step order-automation timeline. Data visibly
 * FLOWS from one step to the next: a travelling token/pulse advances along the
 * rail, reached steps light up, and a spotlight names the active step. Loops +
 * auto-plays, but is also steppable + hoverable (initWorkflow). Horizontal on
 * desktop, reflows to a vertical spine on narrow. IP firewall: WHAT + WHY only.
 * Server-renders a legible, completed-looking first state (works JS-off). */
var WF_STEPS = [
  { t: "Order placed",     d: "A customer checks out — the automation wakes up." },
  { t: "Payment captured", d: "Funds confirm, so the order is real and safe to fulfil." },
  { t: "Inventory updated", d: "Stock counts adjust the instant the sale lands." },
  { t: "Pick list ready",  d: "The items to gather are assembled for whoever's packing." },
  { t: "Packed",           d: "The order is boxed and marked ready to leave." },
  { t: "Shipped",          d: "A label is drawn and the parcel is on its way." },
  { t: "Tracking sent",    d: "The customer gets their tracking link, automatically." },
  { t: "Thank-you note",   d: "A warm, on-brand message goes out — no one lifts a finger." },
  { t: "Loyalty points",   d: "Points land in their account, ready for next time." },
  { t: "Review request",   d: "A little later, a gentle nudge to share how it went." }
];
function workflowsView() {
  return [
    { n: "div", c: "egt-lens-wf", id: "egt-lens-wf", "data-lens-wf": "true",
      i: [
        { n: "div", c: "egt-lens-wf__intro",
          i: [ { n: "span", c: "egt-lens-wf__intro-k", i: "Automation" },
            { n: "span", c: "egt-lens-wf__intro-t", i: "When an order arrives…" } ] },

        { n: "div", c: "egt-lens-wf__rail", role: "group", "aria-label": "Order automation timeline (demo)",
          i: [
            { n: "div", c: "egt-lens-wf__line", "aria-hidden": "true",
              i: [ { n: "span", c: "egt-lens-wf__line-fill" },
                { n: "span", c: "egt-lens-wf__token" } ] },
            { n: "ol", c: "egt-lens-wf__steps",
              i: WF_STEPS.map(function (s, n) {
                return { n: "li", c: "egt-lens-wf__step", "data-step": String(n),
                  style: "--i:" + n, "data-desc": s.d,
                  i: [
                    { n: "button", type: "button", c: "egt-lens-wf__node", tabindex: "-1",
                      "data-wf-go": String(n), "aria-label": (n + 1) + ". " + s.t,
                      i: [ { n: "span", c: "egt-lens-wf__num", i: String(n + 1) },
                        { n: "span", c: "egt-lens-wf__check", "aria-hidden": "true", i: "✓" } ] },
                    { n: "span", c: "egt-lens-wf__slabel", i: s.t }
                  ] };
              }) }
          ] },

        { n: "div", c: "egt-lens-wf__spot", "aria-live": "polite",
          i: [ { n: "span", c: "egt-lens-wf__spot-ix",
              i: [ { n: "b", "data-wf-ix": "true", i: "1" }, " / 10" ] },
            { n: "span", c: "egt-lens-wf__spot-t", "data-wf-title": "true", i: WF_STEPS[0].t },
            { n: "span", c: "egt-lens-wf__spot-d", "data-wf-desc": "true", i: WF_STEPS[0].d } ] }
      ] },
    { n: "p", c: "egt-lens-card__story", i: "Describe the follow-up once; it runs itself, every time — quietly, in the background." }
  ];
}
var VIEWS = {
  site: siteView, marketplace: sellView, reach: reachView,
  engage: engageView, appartisan: nexusView, workflows: workflowsView
};

function lensPanel(l, i) {
  return {
    n: "div", c: "egt-lens__panel" + (i === 0 ? " is-on" : ""), id: "egt-lens-panel-" + l.id,
    role: "tabpanel", "aria-labelledby": "egt-lens-tab-" + l.id,
    "aria-hidden": i === 0 ? "false" : "true", hidden: i === 0 ? undefined : "hidden",
    i: { n: "div", c: "egt-lens-card egt-lens-card--" + l.id, i: VIEWS[l.id]() }
  };
}

/* `mark` (optional): a footnote marker node (S.footnotes().mark(...)) appended
 * to the section title. The former inline egt-pf-note is now that footnote. */
exports.lens = function lens(mark) {
  var title = [ "Do more." ];
  if (mark) { title.push(mark); }
  return {
    n: "section", c: "egt-section egt-pf-lens",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "div", c: "egt-section__head egt-reveal",
          i: [ { n: "p", c: "egt__eyebrow", i: "" },
            { n: "h2", c: "egt-section__title", i: title },
            { n: "p", c: "egt-section__lead", i: "Your business running smoothly. Tell your story. Accept to-go orders. Share with fans. Expand your possibilities — one business object, pointed at every surface you need." } ] },
        { n: "div", c: "egt-pf-lens__stage egt-reveal", id: "egt-lens",
          i: [
            { n: "div", c: "egt-lens__rail", role: "tablist", "aria-label": "Choose a lens",
              i: LENSES.map(lensTab) },
            { n: "div", c: "egt-lens__viewport",
              i: LENSES.map(lensPanel) }
          ] }
      ] }
  };
};

/* =====================================================================
 * MAKE IT YOURS — light live brand controls re-skin a sample card.
 * ===================================================================== */
function swatch(name, hex, on) {
  return { n: "button", c: "egt-miy__swatch" + (on ? " is-on" : ""), type: "button",
    role: "radio", "aria-checked": on ? "true" : "false", "aria-label": name,
    "data-miy-color": hex, style: "--sw:" + hex };
}

/* The three category moods — distinct, approved on-disk imagery used to
 * swap the page's canvas/hero/make-it-yours background per Category.
 *   ceramics → warm/earthy (handcraft, amber light)
 *   coaching → bright/active (kinetic, daylight)
 *   music    → moody/dark (cinematic, deep blue)
 * Each `data-pf-cat` value selects the matching .egt-pf-catbg layer below;
 * the layers crossfade (opacity), instant under reduced motion.
 * Category-matched, undimmed Unsplash photography (pf-cat-*.jpg, with .avif
 * siblings auto-negotiated by egt.avif.js): ceramics = a warm potter's-wheel
 * handcraft scene (Alexandra Slo), coaching = a bright daylight mentoring
 * session (Amy Hirschi), music = a moody cinematic studio (John Matychuk). */
/* ceramics is the default category (set on the platform root), so its image
 * paints first; the other two are off-screen moods until selected. Intrinsic
 * width/height reserve the box (no CLS) and are pulled from the assets on disk. */
var CAT_BG = {
  ceramics: { src: "/imgs/pf-cat-ceramics.jpg", w: 2000, h: 3000 },
  coaching: { src: "/imgs/pf-cat-coaching.jpg", w: 2000, h: 1333 },
  music:    { src: "/imgs/pf-cat-music.jpg",    w: 2000, h: 1333 }
};
exports.CAT_BG = CAT_BG;
var CAT_DEFAULT = "ceramics";

/* The make-it-yours SITE preview's sample content per category — a DIFFERENT
 * sample world from the Lens (which uses "Atelier Vesper" / The Ember Carafe).
 * Each entry reskins the mock site live when the Category control changes
 * (rebound by initMakeItYours via data-miy-bind). Illustrative sample data. */
var MIY_SITE = {
  ceramics: { mark: "K", brand: "Kiln & Co", addr: "kilnandco.studio",
    eyebrow: "New · Stoneware", h: "The Hearth Bowl",
    sub: "Hand-glazed, fired in small batches. A page that loads before they blink — and looks like you.",
    price: "$54", tag: "Small batch" },
  coaching: { mark: "W", brand: "Wren Coaching", addr: "wrencoaching.studio",
    eyebrow: "New · 6-Week Program", h: "Momentum Sessions",
    sub: "One-to-one coaching, booked in a tap. A page that loads before they blink — and looks like you.",
    price: "$240", tag: "Now booking" },
  music: { mark: "H", brand: "Halcyon Records", addr: "halcyonrecords.studio",
    eyebrow: "New · Vinyl", h: "Nightglass — LP",
    sub: "Pressed on heavyweight wax, numbered editions. A page that loads before they blink — and looks like you.",
    price: "$24", tag: "Limited press" }
};
exports.MIY_SITE = MIY_SITE;
var MIY_SITE_NAV = [ "Shop", "About", "Journal", "Contact" ];

/* the make-it-yours SITE preview — modeled on the Lens siteView() (mock browser
 * bar + nav with logo/links/hamburger + content slice + footer + a working
 * mobile-menu toggle), but a DIFFERENT sample brand and with data-miy-bind hooks
 * so the brand controls rebind the content live. Scoped .egt-miy-site so its
 * headings respond to the page-wide data-pf-font register. Reuses the Lens
 * .egt-lens-site styles (accent bits read var(--egt-gold), set by Accent). */
function miySiteView() {
  var d = MIY_SITE[CAT_DEFAULT];
  return {
    n: "div", c: "egt-lens-site egt-miy-site", id: "egt-miy-site", "data-lens-site": "true",
    i: [
      /* ---- the mock browser top bar ---- */
      { n: "div", c: "egt-lens-site__bar", "aria-hidden": "true",
        i: [ { n: "span", c: "egt-lens-site__dot" }, { n: "span", c: "egt-lens-site__dot" },
          { n: "span", c: "egt-lens-site__dot" },
          { n: "span", c: "egt-lens-site__addr", "data-miy-bind": "addr", i: d.addr } ] },

      /* ---- the partial website ---- */
      { n: "div", c: "egt-lens-site__page",
        i: [
          /* nav: logo + desktop links + hamburger */
          { n: "div", c: "egt-lens-site__nav",
            i: [
              { n: "span", c: "egt-lens-site__logo",
                i: [ { n: "span", c: "egt-lens-site__logo-mk", "aria-hidden": "true", "data-miy-bind": "mark", i: d.mark },
                  { n: "span", c: "egt-lens-site__logo-tx", "data-miy-bind": "brand", i: d.brand } ] },
              { n: "nav", c: "egt-lens-site__links", "aria-label": "Sample site navigation (demo)",
                i: MIY_SITE_NAV.map(function (lbl, n) {
                  return { n: "button", type: "button", c: "egt-lens-site__lk" + (n === 0 ? " is-on" : ""),
                    tabindex: "-1", i: lbl };
                }) },
              { n: "button", type: "button", c: "egt-lens-site__burger", "data-lens-site-toggle": "true",
                "aria-expanded": "false", "aria-controls": "egt-miy-site-menu", "aria-label": "Open menu", tabindex: "-1",
                i: [ { n: "span", c: "egt-lens-site__burger-ln", "aria-hidden": "true" },
                  { n: "span", c: "egt-lens-site__burger-ln", "aria-hidden": "true" },
                  { n: "span", c: "egt-lens-site__burger-ln", "aria-hidden": "true" } ] }
            ] },

          /* the mobile menu (revealed by the hamburger) */
          { n: "div", c: "egt-lens-site__menu", id: "egt-miy-site-menu", hidden: "hidden",
            i: MIY_SITE_NAV.map(function (lbl, n) {
              return { n: "button", type: "button", c: "egt-lens-site__menu-lk" + (n === 0 ? " is-on" : ""),
                tabindex: "-1", style: "--egt-i:" + n, i: lbl };
            }) },

          /* content slice: hero art + heading + line + price/cta */
          { n: "div", c: "egt-lens-site__hero",
            i: [
              { n: "div", c: "egt-lens-site__art", "aria-hidden": "true",
                i: { n: "span", c: "egt-lens-site__art-tag", "data-miy-bind": "tag", i: d.tag } },
              { n: "div", c: "egt-lens-site__copy",
                i: [
                  { n: "p", c: "egt-lens-site__eyebrow", "data-miy-bind": "eyebrow", i: d.eyebrow },
                  { n: "h3", c: "egt-lens-site__h", "data-miy-bind": "h", i: d.h },
                  { n: "p", c: "egt-lens-site__sub", "data-miy-bind": "sub", i: d.sub },
                  { n: "div", c: "egt-lens-site__row",
                    i: [ { n: "span", c: "egt-lens-site__price", "data-miy-bind": "price", i: d.price },
                      { n: "button", type: "button", c: "egt-lens-site__cta", tabindex: "-1", i: "Add to cart" } ] }
                ] }
            ] },

          /* a tiny footer */
          { n: "div", c: "egt-lens-site__foot",
            i: [ { n: "span", c: "egt-lens-site__foot-bn", "data-miy-bind": "brand", i: d.brand },
              { n: "span", c: "egt-lens-site__foot-links",
                i: [ { n: "span", c: "egt-lens-site__foot-lk", i: "Shop" },
                  { n: "span", c: "egt-lens-site__foot-lk", i: "About" },
                  { n: "span", c: "egt-lens-site__foot-lk", i: "Journal" } ] },
              { n: "span", c: "egt-lens-site__foot-c", i: "© Studio" } ] }
        ] }
    ]
  };
}
exports.miySiteView = miySiteView;

/* A stacked, crossfading category-mood background layer for a section. Renders
 * all three images; CSS shows the one matching the platform root's data-pf-cat.
 * Undimmed media; contrast still comes from the frosted content above it.
 * Only the DEFAULT mood loads eagerly (visible at first paint); the other two
 * are lazy so their weight (esp. the large music image) doesn't hurt LCP. */
function catBackdrop() {
  return {
    n: "div", c: "egt-pf-catbg", "aria-hidden": "true",
    i: Object.keys(CAT_BG).map(function (cat) {
      var img = CAT_BG[cat];
      var isDefault = cat === CAT_DEFAULT;
      return { n: "img", c: "egt-pf-catbg__img egt-pf-catbg__img--" + cat,
        "data-cat": cat, src: img.src, alt: "", width: img.w, height: img.h,
        loading: isDefault ? undefined : "lazy",
        fetchpriority: isDefault ? undefined : "low",
        decoding: "async" };
    })
  };
}
exports.catBackdrop = catBackdrop;

exports.makeItYours = function makeItYours() {
  return {
    n: "section", c: "egt-section egt-pf-miy",
    i: [ catBackdrop(), { n: "div", c: "egt__shell egt-pf-miy__grid",
      i: [
        { n: "div", c: "egt-pf-miy__copy egt-glass egt-reveal",
          i: [ { n: "p", c: "egt__eyebrow", i: "Make it yours" },
            { n: "h2", c: "egt-section__title", i: "Experience the product." },
            { n: "p", c: "egt-section__lead", i: "Your brand isn't a theme you pick at the end — it runs through everything from the first pixel. Try it." },
            { n: "div", c: "egt-miy__controls",
              i: [
                { n: "div", c: "egt-miy__group",
                  i: [ { n: "span", c: "egt-miy__lbl", id: "egt-miy-color-lbl", i: "Accent" },
                    { n: "div", c: "egt-miy__accent",
                      i: [
                        { n: "div", c: "egt-miy__swatches", role: "radiogroup", "aria-labelledby": "egt-miy-color-lbl",
                          i: [ swatch("Gold", "#C48E2F", true), swatch("Rose", "#C46A7A", false),
                            swatch("Teal", "#3A9D9A", false), swatch("Violet", "#8E7BD6", false),
                            swatch("Slate", "#8A95A5", false) ] },
                        /* custom accent — any color the tenant wants; drives the
                         * same --egt-gold accent var as the presets (initMakeItYours). */
                        { n: "span", c: "egt-miy__custom", "data-miy-custom": "true",
                          i: [
                            { n: "input", type: "color", c: "egt-miy__custom-input", id: "egt-miy-custom",
                              value: "#C48E2F", "aria-label": "Custom accent color",
                              title: "Choose any accent color" },
                            { n: "span", c: "egt-miy__custom-ring", "aria-hidden": "true" } ] } ] } ] },
                { n: "div", c: "egt-miy__group",
                  i: [ { n: "span", c: "egt-miy__lbl", id: "egt-miy-type-lbl", i: "Type" },
                    { n: "div", c: "egt-miy__seg", role: "radiogroup", "aria-labelledby": "egt-miy-type-lbl",
                      i: [
                        { n: "button", c: "egt-miy__opt is-on", type: "button", role: "radio", "aria-checked": "true", "data-miy-font": "serif", i: "Editorial" },
                        { n: "button", c: "egt-miy__opt", type: "button", role: "radio", "aria-checked": "false", "data-miy-font": "sans", i: "Modern" } ] } ] },
                { n: "div", c: "egt-miy__group",
                  i: [ { n: "span", c: "egt-miy__lbl", id: "egt-miy-cat-lbl", i: "Category" },
                    { n: "div", c: "egt-miy__seg", role: "radiogroup", "aria-labelledby": "egt-miy-cat-lbl",
                      i: [
                        { n: "button", c: "egt-miy__opt is-on", type: "button", role: "radio", "aria-checked": "true",
                          "data-miy-cat": "ceramics", i: "Ceramics" },
                        { n: "button", c: "egt-miy__opt", type: "button", role: "radio", "aria-checked": "false",
                          "data-miy-cat": "coaching", i: "Coaching" },
                        { n: "button", c: "egt-miy__opt", type: "button", role: "radio", "aria-checked": "false",
                          "data-miy-cat": "music", i: "Music" } ] } ] }
              ] },
            /* Auto-reset countdown clock. Every change re-arms it; when it lapses
             * the page snaps back to EGT defaults. It's a floating clock in the
             * bottom-right corner (moved to <body> by initMakeItYours so its
             * fixed positioning is honoured), hidden until the first change.
             * Hover reveals a big "reset" X; clicking it reverts immediately;
             * the last 3 seconds pulse; it vanishes once reverted. The ring is a
             * conic-gradient driven by --p (fraction remaining). */
            { n: "button", c: "egt-miy__clock", id: "egt-miy-clock", type: "button", hidden: "hidden",
              "aria-label": "Auto-resets to EGT defaults soon — click to reset now",
              title: "Reset to EGT now",
              i: [
                { n: "span", c: "egt-miy__clock-ring", "aria-hidden": "true" },
                { n: "span", c: "egt-miy__clock-num", id: "egt-miy-clock-num", i: "30" },
                { n: "span", c: "egt-miy__clock-x", "aria-hidden": "true", i: "×" },
                { n: "span", c: "egt-miy__clock-cap", "aria-hidden": "true", i: "Reset" }
              ] } ] },
        { n: "div", c: "egt-pf-miy__preview egt-reveal egt-reveal--right",
          i: miySiteView() }
      ] } ]
  };
};

/* =====================================================================
 * YOUR TEAM — role-based access + invite, as ONE team console (WP-6a).
 * Left: the team roster; each member carries a real-feeling role. Selecting a
 * member MORPHS the right-hand ACCESS PREVIEW — the surfaces they can open and
 * the actions they can take. The Invite tab composes a role-SCOPED invite (the
 * same role set); "Send" is a pure client animation that drops a Pending
 * teammate into the roster — so role + invite read as one team system.
 *
 * The role model is authored HERE (single source of truth) and travels to the
 * client on each roster button as data-surfaces / data-actions, so the client
 * never re-declares the rules — it just toggles what the DOM already carries.
 * IP firewall: WHAT + WHY only — the capability (scoped, role-based access;
 * bring people in with the right reach), never the mechanism. Sample data only;
 * no live accounts, no real invitations, no server round-trip.
 * ===================================================================== */
var TEAM_SURFACES = [
  { id: "site", label: "Site" },
  { id: "shop", label: "Marketplace" },
  { id: "orders", label: "Orders" },
  { id: "customers", label: "Customers" },
  { id: "reach", label: "Reach" },
  { id: "engage", label: "Engage" },
  { id: "flows", label: "Workflows" },
  { id: "team", label: "Team" },
  { id: "billing", label: "Billing" }
];
var TEAM_ACTIONS = [
  { id: "publish", label: "Publish pages" },
  { id: "products", label: "Edit products" },
  { id: "fulfil", label: "Fulfil & ship orders" },
  { id: "refund", label: "Refund orders" },
  { id: "email", label: "Email customers" },
  { id: "export", label: "Export customer list" },
  { id: "invite", label: "Invite teammates" },
  { id: "billpay", label: "Manage billing" }
];
/* the roles, from most to least reach. Each lists the surface ids it can open
 * and the action ids it can take; everything else renders locked. Real-feeling
 * separation of duties (content vs money vs fulfilment vs read-only). */
var TEAM_ROLES = {
  owner:   { label: "Owner", blurb: "Full access — the whole business, every surface.",
    surfaces: [ "site", "shop", "orders", "customers", "reach", "engage", "flows", "team", "billing" ],
    actions:  [ "publish", "products", "fulfil", "refund", "email", "export", "invite", "billpay" ] },
  manager: { label: "Manager", blurb: "Runs the day-to-day — everything but ownership and the bank.",
    surfaces: [ "site", "shop", "orders", "customers", "reach", "engage", "flows", "team" ],
    actions:  [ "publish", "products", "fulfil", "refund", "email", "export", "invite" ] },
  editor:  { label: "Editor", blurb: "Makes the content and the store — no customer data, no money moved.",
    surfaces: [ "site", "shop", "reach", "engage", "flows" ],
    actions:  [ "publish", "products", "email" ] },
  fulfil:  { label: "Fulfilment", blurb: "Sees only what's needed to pick, pack and ship.",
    surfaces: [ "shop", "orders" ],
    actions:  [ "fulfil" ] },
  viewer:  { label: "Viewer", blurb: "Read-only — can look, never touch.",
    surfaces: [ "site", "shop", "orders", "customers" ],
    actions:  [] }
};
var TEAM_ROLE_ORDER = [ "owner", "manager", "editor", "fulfil", "viewer" ];
var TEAM_MEMBERS = [
  { nm: "Maya Osei",      title: "Founder",         role: "owner",   ini: "MO" },
  { nm: "Dorian Vale",    title: "Operations",      role: "manager", ini: "DV" },
  { nm: "Priya Raman",    title: "Content & Store", role: "editor",  ini: "PR" },
  { nm: "Theo Marsh",     title: "Fulfilment",      role: "fulfil",  ini: "TM" },
  { nm: "Sage Whitlock",  title: "Bookkeeper",      role: "viewer",  ini: "SW" }
];
var TEAM_INV_DEFAULT = "editor";

/* a crisp check OR lock glyph (24-box, currentColor stroke). Both are rendered
 * into every capability chip; CSS shows the one that matches the on/off state,
 * so a role switch never needs JS to rebuild icons (crawlable + no-JS safe). */
function teamIcon(kind, cls) {
  var base = { n: "svg", c: "egt-pf-team__ic-svg" + (cls ? " " + cls : ""), viewBox: "0 0 24 24",
    fill: "none", stroke: "currentColor", "stroke-width": "2", "stroke-linecap": "round",
    "stroke-linejoin": "round", "aria-hidden": "true", focusable: "false" };
  if (kind === "lock") {
    base.i = [ { n: "rect", x: "5", y: "11", width: "14", height: "9", rx: "2" },
      { n: "path", d: "M8 11V8a4 4 0 0 1 8 0v3" } ];
  } else {
    base.i = [ { n: "path", d: "M20 6 9 17l-5-5" } ];
  }
  return base;
}
function teamCapIcons() { return [ teamIcon("check", "egt-pf-team__ic--check"), teamIcon("lock", "egt-pf-team__ic--lock") ]; }

function teamCaps(role) {
  var allow = TEAM_ROLES[role].surfaces;
  return TEAM_SURFACES.map(function (s, idx) {
    var on = allow.indexOf(s.id) !== -1;
    return { n: "li", c: "egt-pf-team__cap" + (on ? " is-on" : " is-off"), "data-cap": s.id, style: "--egt-i:" + idx,
      i: [ { n: "span", c: "egt-pf-team__cap-ic", "aria-hidden": "true", i: teamCapIcons() },
        { n: "span", c: "egt-pf-team__cap-lb", i: s.label } ] };
  });
}
function teamActs(role) {
  var allow = TEAM_ROLES[role].actions;
  return TEAM_ACTIONS.map(function (a, idx) {
    var on = allow.indexOf(a.id) !== -1;
    return { n: "li", c: "egt-pf-team__act" + (on ? " is-on" : " is-off"), "data-act": a.id, style: "--egt-i:" + idx,
      i: [ { n: "span", c: "egt-pf-team__act-ic", "aria-hidden": "true", i: teamCapIcons() },
        { n: "span", c: "egt-pf-team__act-lb", i: a.label } ] };
  });
}
function teamMember(m, i) {
  var r = TEAM_ROLES[m.role];
  return { n: "button", type: "button", c: "egt-pf-team__member" + (i === 0 ? " is-on" : ""),
    role: "tab", "aria-selected": i === 0 ? "true" : "false", tabindex: i === 0 ? "0" : "-1",
    "data-role": m.role, "data-role-label": r.label, "data-role-blurb": r.blurb,
    "data-surfaces": r.surfaces.join(" "), "data-actions": r.actions.join(" "),
    i: [
      { n: "span", c: "egt-pf-team__avatar", "aria-hidden": "true", i: m.ini },
      { n: "span", c: "egt-pf-team__member-main",
        i: [ { n: "span", c: "egt-pf-team__member-nm", i: m.nm },
          { n: "span", c: "egt-pf-team__member-ti", i: m.title } ] },
      { n: "span", c: "egt-pf-team__member-role", i: r.label }
    ] };
}

exports.team = function team() {
  var d = TEAM_MEMBERS[0];
  var dr = TEAM_ROLES[d.role];
  var ir = TEAM_ROLES[TEAM_INV_DEFAULT];
  return {
    n: "section", c: "egt-section egt-pf-team",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "div", c: "egt-section__head egt-reveal",
          i: [ { n: "p", c: "egt__eyebrow", i: "Role-based access" },
            { n: "h2", c: "egt-section__title", i: "Everyone sees exactly what they should." },
            { n: "p", c: "egt-section__lead", i: "Pick a teammate to see what their role opens up — the surfaces they can enter and the moves they can make. Then invite someone new with exactly the right reach. One team, one source of truth." } ] },

        { n: "div", c: "egt-glass egt-glass--wide egt-pf-team__console egt-reveal", id: "egt-team", "data-tab": "access",
          i: [
            /* console header — sample brand, live people count, view tabs */
            { n: "div", c: "egt-pf-team__bar",
              i: [
                { n: "span", c: "egt-pf-team__brand",
                  i: [ { n: "span", c: "egt-pf-team__brand-mk", "aria-hidden": "true", i: "A" },
                    { n: "span", c: "egt-pf-team__brand-tx", i: "Atelier Vesper · Team" } ] },
                { n: "span", c: "egt-pf-team__count", i: [ { n: "b", "data-team-count": "true", i: String(TEAM_MEMBERS.length) }, " people" ] },
                { n: "div", c: "egt-pf-team__tabs", role: "tablist", "aria-label": "Team view",
                  i: [
                    { n: "button", type: "button", c: "egt-pf-team__tab is-on", "data-team-tab": "access",
                      role: "tab", "aria-selected": "true", tabindex: "0", "aria-controls": "egt-team-access", i: "Access" },
                    { n: "button", type: "button", c: "egt-pf-team__tab", "data-team-tab": "invite",
                      role: "tab", "aria-selected": "false", tabindex: "-1", "aria-controls": "egt-team-invite", i: "Invite" }
                  ] }
              ] },

            { n: "div", c: "egt-pf-team__body",
              i: [
                /* LEFT — the roster IS the role selector (a tablist of people) */
                { n: "div", c: "egt-pf-team__roster", role: "tablist", "aria-label": "Choose a teammate", "data-team-roster": "true",
                  i: TEAM_MEMBERS.map(teamMember) },

                /* RIGHT — access preview + invite composer (one at a time) */
                { n: "div", c: "egt-pf-team__panels",
                  i: [
                    /* ---- ACCESS PREVIEW (default) ---- */
                    { n: "div", c: "egt-pf-team__access is-on", id: "egt-team-access", "data-team-panel": "access",
                      role: "tabpanel", "aria-live": "polite",
                      i: [
                        { n: "div", c: "egt-pf-team__role",
                          i: [ { n: "span", c: "egt-pf-team__role-badge", "data-team-role-label": "true", i: dr.label },
                            { n: "p", c: "egt-pf-team__role-blurb", "data-team-role-blurb": "true", i: dr.blurb } ] },
                        { n: "div", c: "egt-pf-team__meter",
                          i: [ { n: "span", c: "egt-pf-team__meter-i",
                              i: [ { n: "b", "data-team-surf-n": "true", i: String(dr.surfaces.length) }, " of " + TEAM_SURFACES.length + " areas" ] },
                            { n: "span", c: "egt-pf-team__meter-sep", "aria-hidden": "true", i: "·" },
                            { n: "span", c: "egt-pf-team__meter-i",
                              i: [ { n: "b", "data-team-act-n": "true", i: String(dr.actions.length) }, " of " + TEAM_ACTIONS.length + " actions" ] } ] },
                        { n: "div", c: "egt-pf-team__grp",
                          i: [ { n: "p", c: "egt-pf-team__grp-h", i: "Can open" },
                            { n: "ul", c: "egt-pf-team__caps", "data-team-caps": "true", i: teamCaps(d.role) } ] },
                        { n: "div", c: "egt-pf-team__grp",
                          i: [ { n: "p", c: "egt-pf-team__grp-h", i: "Can do" },
                            { n: "ul", c: "egt-pf-team__acts", "data-team-acts": "true", i: teamActs(d.role) } ] }
                      ] },

                    /* ---- INVITE COMPOSER ---- */
                    { n: "div", c: "egt-pf-team__invite", id: "egt-team-invite", "data-team-panel": "invite",
                      role: "tabpanel", hidden: "hidden",
                      i: [
                        { n: "div", c: "egt-pf-team__inv-form",
                          i: [
                            { n: "label", c: "egt-pf-team__inv-lb", "for": "egt-team-inv-name", i: "Name" },
                            { n: "input", c: "egt-pf-team__inv-in", id: "egt-team-inv-name", type: "text",
                              value: "Wren Ellison", autocomplete: "off", spellcheck: "false", "data-team-inv-name": "true" },
                            { n: "label", c: "egt-pf-team__inv-lb", "for": "egt-team-inv-email", i: "Email" },
                            { n: "input", c: "egt-pf-team__inv-in", id: "egt-team-inv-email", type: "text",
                              value: "wren@ateliervesper.studio", autocomplete: "off", spellcheck: "false", "data-team-inv-email": "true" },
                            { n: "span", c: "egt-pf-team__inv-lb", id: "egt-team-inv-role-lbl", i: "Role" },
                            { n: "div", c: "egt-pf-team__inv-roles", role: "radiogroup", "aria-labelledby": "egt-team-inv-role-lbl", "data-team-inv-roles": "true",
                              i: TEAM_ROLE_ORDER.map(function (rid) {
                                var on = rid === TEAM_INV_DEFAULT;
                                return { n: "button", type: "button", c: "egt-pf-team__chip" + (on ? " is-on" : ""),
                                  role: "radio", "aria-checked": on ? "true" : "false", tabindex: on ? "0" : "-1",
                                  "data-inv-role": rid, i: TEAM_ROLES[rid].label };
                              }) }
                          ] },
                        /* live, role-scoped preview of the invitation */
                        { n: "div", c: "egt-pf-team__inv-preview", "data-team-inv-preview": "true",
                          i: [
                            { n: "div", c: "egt-pf-team__inv-card",
                              i: [
                                { n: "span", c: "egt-pf-team__inv-tag", i: "Invitation" },
                                { n: "p", c: "egt-pf-team__inv-line",
                                  i: [ { n: "b", "data-inv-pv-name": "true", i: "Wren Ellison" }, ", you're invited to ", { n: "b", i: "Atelier Vesper" }, "." ] },
                                { n: "p", c: "egt-pf-team__inv-scope",
                                  i: [ "Joining as ", { n: "b", "data-inv-pv-role": "true", i: ir.label },
                                    " — can open ", { n: "b", "data-inv-pv-surf": "true", i: String(ir.surfaces.length) },
                                    " areas, do ", { n: "b", "data-inv-pv-act": "true", i: String(ir.actions.length) }, " actions." ] },
                                { n: "p", c: "egt-pf-team__inv-note", i: "They'll get a secure email link — access is scoped to their role from the first sign-in." }
                              ] },
                            { n: "button", type: "button", c: "egt-pf-team__inv-send", "data-team-inv-send": "true", i: "Send invite" }
                          ] },
                        /* success state (revealed on Send) */
                        { n: "div", c: "egt-pf-team__inv-done", "data-team-inv-done": "true", hidden: "hidden", "aria-live": "polite",
                          i: [
                            { n: "span", c: "egt-pf-team__inv-check", "aria-hidden": "true", i: teamIcon("check") },
                            { n: "p", c: "egt-pf-team__inv-done-h", i: "Invite sent." },
                            { n: "p", c: "egt-pf-team__inv-done-s", "data-team-inv-done-s": "true",
                              i: "They'll join with exactly the reach you chose — added to your team as Pending." },
                            { n: "button", type: "button", c: "egt-pf-team__inv-again", "data-team-inv-again": "true", i: "Invite another" }
                          ] }
                      ] }
                  ] }
              ] }
          ] },

        { n: "p", c: "egt-pf-note egt-reveal", i: "Illustrative — sample team. No real accounts, and no invitations are sent." }
      ] }
  };
};

/* =====================================================================
 * WIZARDS → WORKFLOWS → DASHBOARDS (WP-6b) — the "collect everything, see
 * everything" story, told as ONE driveable beat.
 *
 * LEFT: a gorgeous multistep intake wizard the visitor clicks through — step
 * pills that expand to name the step (modelled on the platform's real
 * ua.wizard step-nav: uat/scss/wizard.scss .ua-wizard-step-pill), a progress
 * bar, fields that MORPH per step, light validation with a shake, and a
 * completion "hero" (a big check + a summary) — the same polish as the
 * reserve + Forge wizards the visitor has already used on this very site.
 * RIGHT: a small live dashboard (stat widgets + a mini chart + a recent
 * list). When the wizard is submitted the request LANDS in the dashboard —
 * a row appears and a metric ticks up — so "collect everything → see
 * everything" is something you watch happen, not a claim.
 * BELOW: an honest proof strip (6.4) — the reservation flow behind Join Beta
 * and the Forge intake are built on this SAME wizard engine (real today; the
 * self-serve platform is on its way).
 *
 * IP firewall: WHAT + WHY only — gorgeous multistep collection that feeds a
 * dashboard, never HOW it's built. Sample data only; nothing is submitted and
 * no live account is touched. Reuses the .egt-pf-dash styling from productFrame
 * for consistency, with its own id/data so productFrame is untouched.
 * ===================================================================== */
/* the wizard's steps (the last is the auto "review" summary, like the real
 * wizard's summary step). Each carries a short pill label. */
var WIZ_STEPS = [
  { key: "who",     pill: "You",     title: "Who's this for?",
    lead: "Start with the basics — this is the same first step your customers see." },
  { key: "what",    pill: "Project", title: "What are you making?",
    lead: "Pick the kind of request. The next step adapts to the answer." },
  { key: "details", pill: "Details", title: "A few details.",
    lead: "Scope and timing — enough to route it to the right place." },
  { key: "review",  pill: "Review",  title: "Look good?",
    lead: "One glance to confirm, then it's on its way." }
];
/* the choices for the middle steps (sample "Atelier Vesper" world, shared with
 * the Lens / dashboard for cohesion). data-* carry the readable labels so the
 * review step + dashboard can echo them without re-declaring the map. */
var WIZ_TYPES = [
  { id: "piece",     label: "A custom piece",   meta: "One-of-a-kind, made to order",  dash: "Custom" },
  { id: "workshop",  label: "A workshop seat",  meta: "Learn at the wheel, in studio", dash: "Workshop" },
  { id: "wholesale", label: "A wholesale order", meta: "Stock your shelves",           dash: "Wholesale" }
];
var WIZ_SCALE = [
  { id: "one",        label: "A single piece" },
  { id: "set",        label: "A small set" },
  { id: "collection", label: "A full collection" }
];
var WIZ_WHEN = [
  { id: "month",   label: "This month" },
  { id: "quarter", label: "This quarter" },
  { id: "flex",    label: "I'm flexible" }
];

/* a number/label step pill, mirroring the real .ua-wizard-step-pill nav. */
function wizPill(s, i) {
  return {
    n: "button", type: "button", c: "egt-wiz__pill-btn" + (i === 0 ? " is-on" : ""),
    "data-wiz-go": String(i), "aria-label": "Step " + (i + 1) + ": " + s.pill,
    "aria-current": i === 0 ? "step" : undefined, tabindex: "-1",
    i: [ { n: "span", c: "egt-wiz__pill", i: String(i + 1) },
      { n: "span", c: "egt-wiz__pill-lb", i: s.pill } ]
  };
}

/* a choice-card radiogroup (used for type/scale/when). `variant` sizes it. */
function wizChoices(group, list, variant, defId) {
  return {
    n: "div", c: "egt-wiz__choices egt-wiz__choices--" + variant, role: "radiogroup",
    "aria-label": group, "data-wiz-choice": group,
    i: list.map(function (o) {
      var on = o.id === defId;
      return {
        n: "button", type: "button", c: "egt-wiz__choice" + (on ? " is-on" : ""),
        role: "radio", "aria-checked": on ? "true" : "false", tabindex: on ? "0" : "-1",
        "data-wiz-val": o.id, "data-wiz-label": o.label, "data-wiz-dash": o.dash || o.label,
        i: [ { n: "span", c: "egt-wiz__choice-lb", i: o.label } ].concat(
          o.meta ? [ { n: "span", c: "egt-wiz__choice-meta", i: o.meta } ] : []) };
    })
  };
}

/* the step bodies (fields morph between these). */
function wizStepBody(key) {
  if (key === "who") {
    return [
      { n: "div", c: "egt-wiz__field",
        i: [ { n: "label", c: "egt-wiz__lb", "for": "egt-wiz-name", i: "Name" },
          { n: "input", c: "egt-wiz__in", id: "egt-wiz-name", type: "text", value: "Wren Ellison",
            autocomplete: "off", spellcheck: "false", "data-wiz-field": "name", "aria-required": "true" } ] },
      { n: "div", c: "egt-wiz__field",
        i: [ { n: "label", c: "egt-wiz__lb", "for": "egt-wiz-email", i: "Email" },
          { n: "input", c: "egt-wiz__in", id: "egt-wiz-email", type: "text", value: "wren@studio.example",
            autocomplete: "off", spellcheck: "false", "data-wiz-field": "email", "aria-required": "true" } ] }
    ];
  }
  if (key === "what") {
    return [ wizChoices("type", WIZ_TYPES, "cards", "piece") ];
  }
  if (key === "details") {
    return [
      { n: "p", c: "egt-wiz__grp-h", i: "Scale" },
      wizChoices("scale", WIZ_SCALE, "pills", "set"),
      { n: "p", c: "egt-wiz__grp-h", i: "Timing" },
      wizChoices("when", WIZ_WHEN, "pills", "quarter")
    ];
  }
  /* review — a summary that the client keeps in sync with the picks above */
  return [
    { n: "ul", c: "egt-wiz__summary",
      i: [
        { n: "li", c: "egt-wiz__sum-row",
          i: [ { n: "span", c: "egt-wiz__sum-k", i: "From" },
            { n: "span", c: "egt-wiz__sum-v", "data-wiz-review": "name", i: "Wren Ellison" } ] },
        { n: "li", c: "egt-wiz__sum-row",
          i: [ { n: "span", c: "egt-wiz__sum-k", i: "Email" },
            { n: "span", c: "egt-wiz__sum-v", "data-wiz-review": "email", i: "wren@studio.example" } ] },
        { n: "li", c: "egt-wiz__sum-row",
          i: [ { n: "span", c: "egt-wiz__sum-k", i: "Project" },
            { n: "span", c: "egt-wiz__sum-v", "data-wiz-review": "type", i: "A custom piece" } ] },
        { n: "li", c: "egt-wiz__sum-row",
          i: [ { n: "span", c: "egt-wiz__sum-k", i: "Scale" },
            { n: "span", c: "egt-wiz__sum-v", "data-wiz-review": "scale", i: "A small set" } ] },
        { n: "li", c: "egt-wiz__sum-row",
          i: [ { n: "span", c: "egt-wiz__sum-k", i: "Timing" },
            { n: "span", c: "egt-wiz__sum-v", "data-wiz-review": "when", i: "This quarter" } ] }
      ] },
    { n: "p", c: "egt-wiz__sum-note", i: "On submit, a workflow tags the request and it lands in your dashboard — automatically." }
  ];
}

/* the DASHBOARD instance — its own id + data (productFrame's egt-pf-dash is
 * untouched). Reuses the .egt-pf-dash* look for consistency, adds the
 * .egt-wiz-dash hook for build-in + the live-update the wizard drives. */
var WIZ_DASH_STATS = [
  { k: "Requests · wk", num: 128, d: "+9",   dir: "up" },
  { k: "Awaiting",      num: 12,  d: "−3",   dir: "down" },
  { k: "Booked",        num: 34,  d: "+6",   dir: "up" },
  { k: "Reply rate",    v: "96%", d: "+2%",  dir: "up" }
];
var WIZ_DASH_BARS = [ 44, 52, 40, 61, 55, 68, 63, 78 ];
var WIZ_DASH_ROWS = [
  { who: "Nadia Brooks", type: "Custom",    st: "New",      cls: "ready" },
  { who: "Owen Vasquez", type: "Workshop",  st: "Replied",  cls: "paid" },
  { who: "Lila Fenn",    type: "Wholesale", st: "Booked",   cls: "pack" }
];
function wizardDashboard() {
  return {
    n: "div", c: "egt-pf-dash egt-wiz-dash", id: "egt-wiz-dash", role: "img",
    "aria-label": "Sample intake dashboard — requests this week, a requests chart, and recent intakes. Illustrative data.",
    i: [
      { n: "div", c: "egt-pf-dash__bar", "aria-hidden": "true",
        i: [
          { n: "span", c: "egt-pf-dash__brand",
            i: [ { n: "span", c: "egt-pf-dash__brand-mk", i: "A" },
              { n: "span", c: "egt-pf-dash__brand-tx", i: "Atelier Vesper" } ] },
          { n: "nav", c: "egt-pf-dash__nav",
            i: [ { n: "span", c: "egt-pf-dash__nav-i is-on", i: "Intake" },
              { n: "span", c: "egt-pf-dash__nav-i", i: "Bookings" } ] }
        ] },
      { n: "div", c: "egt-pf-dash__body", "aria-hidden": "true",
        i: [
          { n: "div", c: "egt-pf-dash__stats",
            i: WIZ_DASH_STATS.map(function (s, idx) {
              var val;
              if (typeof s.num === "number") {
                val = { n: "span", c: "egt-pf-dash__stat-v", "data-count-to": s.num, "data-count-group": "1", i: dashGroup(s.num) };
              } else {
                val = { n: "span", c: "egt-pf-dash__stat-v", i: s.v };
              }
              var extra = idx === 0 ? { "data-wiz-dash-metric": "true" } : {};
              var stat = { n: "div", c: "egt-pf-dash__stat", style: "--egt-i:" + idx,
                i: [ { n: "span", c: "egt-pf-dash__stat-k", i: s.k }, val,
                  { n: "span", c: "egt-pf-dash__stat-d egt-pf-dash__stat-d--" + s.dir,
                    i: [ { n: "span", c: "egt-pf-dash__stat-arrow", i: s.dir === "up" ? "▲" : "▼" }, s.d ] } ] };
              Object.keys(extra).forEach(function (k) { stat[k] = extra[k]; });
              return stat;
            }) },
          { n: "div", c: "egt-pf-dash__split",
            i: [
              { n: "div", c: "egt-pf-dash__panel egt-pf-dash__panel--chart",
                i: [
                  { n: "div", c: "egt-pf-dash__panel-hd",
                    i: [ { n: "span", c: "egt-pf-dash__panel-t", i: "Requests" },
                      { n: "span", c: "egt-pf-dash__panel-sub", i: "Last 8 weeks" } ] },
                  { n: "div", c: "egt-pf-dash__chart",
                    i: { n: "svg", c: "egt-pf-dash__chart-svg", viewBox: "0 0 240 96",
                      preserveAspectRatio: "none", focusable: "false",
                      i: WIZ_DASH_BARS.map(function (h, n) {
                        var bw = 240 / WIZ_DASH_BARS.length;
                        var x = n * bw + bw * 0.22;
                        var w = bw * 0.56;
                        var bh = 96 * (h / 100);
                        return { n: "rect", c: "egt-pf-dash__bar" + (n === WIZ_DASH_BARS.length - 1 ? " is-peak" : ""),
                          style: "--egt-i:" + n, x: x.toFixed(2), y: (96 - bh).toFixed(2),
                          width: w.toFixed(2), height: bh.toFixed(2), rx: "1.5" };
                      }) } }
                ] },
              { n: "div", c: "egt-pf-dash__panel egt-pf-dash__panel--list",
                i: [
                  { n: "div", c: "egt-pf-dash__panel-hd",
                    i: [ { n: "span", c: "egt-pf-dash__panel-t", i: "Recent intakes" },
                      { n: "span", c: "egt-pf-dash__panel-sub", i: "Today" } ] },
                  { n: "ul", c: "egt-pf-dash__rows", "data-wiz-dash-rows": "true",
                    i: WIZ_DASH_ROWS.map(function (r) {
                      return { n: "li", c: "egt-pf-dash__row",
                        i: [
                          { n: "span", c: "egt-pf-dash__row-item", i: r.who },
                          { n: "span", c: "egt-pf-dash__row-amt", i: r.type },
                          { n: "span", c: "egt-pf-dash__pill egt-pf-dash__pill--" + r.cls,
                            i: [ { n: "span", c: "egt-pf-dash__pill-dot" }, r.st ] }
                        ] };
                    }) }
                ] }
            ] }
        ] }
    ]
  };
}

exports.wizards = function wizards() {
  return {
    n: "section", c: "egt-section egt-pf-wiz",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "div", c: "egt-section__head egt-reveal",
          i: [ { n: "p", c: "egt__eyebrow", i: "Wizards → workflows → dashboards" },
            { n: "h2", c: "egt-section__title", i: "Fill it in — watch it land." },
            { n: "p", c: "egt-section__lead", i: "A gorgeous multistep form on one side; a live dashboard on the other. Click through the wizard and submit — the request flows straight into the dashboard, the way everything you collect turns into something you can see." } ] },

        { n: "div", c: "egt-pf-wiz__stage egt-reveal",
          i: [
            /* ---- LEFT: the interactive multistep wizard ---- */
            { n: "div", c: "egt-glass egt-pf-wiz__panel", id: "egt-wiz", "data-wiz": "true", "data-wiz-current": "0",
              i: [
                /* step pills (expand to name the step) */
                { n: "div", c: "egt-wiz__nav", role: "list", "aria-label": "Wizard steps",
                  i: WIZ_STEPS.map(wizPill) },
                /* progress bar */
                { n: "div", c: "egt-wiz__progress", "aria-hidden": "true",
                  i: { n: "span", c: "egt-wiz__progress-fill", "data-wiz-progress": "true", style: "width:0%" } },

                /* the morphing step bodies */
                { n: "div", c: "egt-wiz__flow",
                  i: WIZ_STEPS.map(function (s, i) {
                    return { n: "div", c: "egt-wiz__step" + (i === 0 ? " is-on" : ""), "data-wiz-step": String(i),
                      hidden: i === 0 ? undefined : "hidden",
                      i: [
                        { n: "p", c: "egt-wiz__kicker", i: "Step " + (i + 1) + " of " + WIZ_STEPS.length },
                        { n: "h3", c: "egt-wiz__title", i: s.title },
                        { n: "p", c: "egt-wiz__lead", i: s.lead },
                        { n: "div", c: "egt-wiz__body", i: wizStepBody(s.key) }
                      ] };
                  }) },

                /* completion hero (revealed on submit) */
                { n: "div", c: "egt-wiz__done", "data-wiz-done": "true", hidden: "hidden", "aria-live": "polite",
                  i: [
                    { n: "span", c: "egt-wiz__done-ic", "aria-hidden": "true", i: teamIcon("check") },
                    { n: "h3", c: "egt-wiz__done-h", i: "Request received." },
                    { n: "p", c: "egt-wiz__done-s", "data-wiz-done-s": "true",
                      i: "It's tagged, a workflow's handling the follow-up, and it just landed in your dashboard. That was easy." },
                    { n: "button", type: "button", c: "egt-wiz__restart", "data-wiz-restart": "true", i: "Run it again" }
                  ] },

                /* footer — Back / Continue (Continue becomes Submit on review) */
                { n: "div", c: "egt-wiz__footer", "data-wiz-footer": "true",
                  i: [
                    { n: "button", type: "button", c: "egt-wiz__back", "data-wiz-back": "true", disabled: "disabled", i: "Back" },
                    { n: "button", type: "button", c: "egt-wiz__next", "data-wiz-next": "true", i: "Continue" }
                  ] }
              ] },

            /* ---- RIGHT: the live dashboard the wizard feeds ---- */
            { n: "div", c: "egt-pf-wiz__dashwrap",
              i: [
                { n: "p", c: "egt-pf-wiz__dash-cap",
                  i: [ { n: "span", c: "egt-pf-wiz__dash-dot", "aria-hidden": "true" }, "Live dashboard" ] },
                { n: "div", c: "egt-glass egt-pf-wiz__dash", i: wizardDashboard() }
              ] }
          ] },

        { n: "p", c: "egt-pf-note egt-reveal", i: "Illustrative — sample data. Nothing here is submitted, and no live account is touched." }
      ] }
  };
};

/* =====================================================================
 * SAME ENGINE (WP-N6.5) — the "you've already used one" proof, promoted
 * from a small strip inside the Wizards section to its OWN deliberate
 * half-viewport beat: generous space, strong typography, and the two
 * wizards the visitor has already touched on this very site (the reserve
 * flow + the Forge intake) linked as living evidence. Meaning + links are
 * preserved; only the frame around it grew.
 * ===================================================================== */
exports.sameEngine = function sameEngine() {
  return {
    n: "section", c: "egt-section egt-section--center egt-pf-engine",
    i: { n: "div", c: "egt__shell egt-pf-engine__shell egt-reveal",
      i: [
        { n: "p", c: "egt__eyebrow", i: "The same engine" },
        { n: "h2", c: "egt-pf-engine__h",
          i: [ "You've already used one. ",
            { n: "span", c: "egt__gold", i: "This is the same engine." } ] },
        { n: "p", c: "egt-pf-engine__s", i: "Every wizard on this very site is built on it — the reservation flow that holds your founding spot, the Forge intake that starts your project. Real, working, today. The self-serve builder that lets you make your own is on its way." },
        { n: "div", c: "egt-pf-engine__cards",
          i: [
            { n: "a", href: "/reserve", c: "egt-pf-engine__card egt-lift",
              i: [
                { n: "span", c: "egt-pf-engine__tag", i: "Reservation flow" },
                { n: "span", c: "egt-pf-engine__nm", i: "Join Beta" },
                { n: "span", c: "egt-pf-engine__cp", i: "The multistep flow that holds your founding spot — a wizard, start to finish." },
                { n: "span", c: "egt-pf-engine__go", "aria-hidden": "true", i: "Open it →" }
              ] },
            { n: "a", href: "/forge", c: "egt-pf-engine__card egt-lift",
              i: [
                { n: "span", c: "egt-pf-engine__tag", i: "Project intake" },
                { n: "span", c: "egt-pf-engine__nm", i: "The Forge intake" },
                { n: "span", c: "egt-pf-engine__cp", i: "Tell us about your project across a few gorgeous steps — the same wizard, put to work." },
                { n: "span", c: "egt-pf-engine__go", "aria-hidden": "true", i: "See it →" }
              ] }
          ] }
      ] }
  };
};

/* =====================================================================
 * UNIVERSE SEARCH — the interactive product / command-palette demo.
 * One index, one language, everything connected. Mixed object types
 * (Product · Customer · Order · Page · Message · Workflow) over a hardcoded
 * sample dataset, server-rendered as real UA-JSON (crawlable, works JS-off).
 * initSearch() filters the pre-rendered rows live; no backend, no live call.
 * Same "Atelier Vesper" sample world as the Lens for cohesion.
 * ===================================================================== */
var SEARCH_ROWS = [
  { type: "Product",  title: "The Ember Carafe",
    meta: "Ceramics · $68 · 14 in stock", icon: "◈",
    terms: "ember carafe ceramics stoneware product mug pour $68 vesper" },
  { type: "Product",  title: "Nightglass Tumbler — set of 2",
    meta: "Ceramics · $120 · 6 in stock", icon: "◈",
    terms: "nightglass tumbler set glass ceramics product $120 vesper" },
  { type: "Customer", title: "A customer who bought the Ember Carafe",
    meta: "3 orders · joined this season", icon: "○",
    terms: "customer bought ember carafe member repeat returning person buyer" },
  { type: "Order",    title: "Order #1042",
    meta: "Ember Carafe · paid · shipped", icon: "▤",
    terms: "order 1042 ember carafe paid shipped fulfilled receipt purchase" },
  { type: "Order",    title: "Order #1048",
    meta: "Nightglass set · paid · packing", icon: "▤",
    terms: "order 1048 nightglass set paid packing fulfilled receipt purchase" },
  { type: "Page",     title: "ateliervesper.studio",
    meta: "Home · published · brand-aware", icon: "▦",
    terms: "page site ateliervesper studio home published profile presence" },
  { type: "Page",     title: "The Ember Carafe — product page",
    meta: "Storefront · published", icon: "▦",
    terms: "page product carafe storefront pdp published ember site" },
  { type: "Message",  title: "Your Ember Carafe, and what pairs with it",
    meta: "Not sent · awaiting your approval", icon: "✉",
    terms: "message email newsletter ember carafe pairs unsent reach compose" },
  { type: "Workflow", title: "When an order arrives…",
    meta: "Live · thank-you, tag, update", icon: "⟳",
    terms: "workflow automation order arrives thank you tag dashboard runs trigger" },
  { type: "Workflow", title: "Low-stock reminder",
    meta: "Live · notify when under 5", icon: "⟳",
    terms: "workflow automation low stock reminder inventory notify runs trigger" }
];

function searchRow(r, idx) {
  return {
    n: "li", c: "egt-pf-search__row", role: "option", id: "egt-search-opt-" + idx,
    "aria-selected": "false", "data-search": r.terms.toLowerCase(),
    i: [
      { n: "span", c: "egt-pf-search__icon", "aria-hidden": "true", i: r.icon },
      { n: "span", c: "egt-pf-search__main",
        i: [ { n: "span", c: "egt-pf-search__title", i: r.title },
          { n: "span", c: "egt-pf-search__meta", i: r.meta } ] },
      { n: "span", c: "egt-pf-search__chip egt-pf-search__chip--" + r.type.toLowerCase(), i: r.type }
    ]
  };
}

/* `mark` (optional): footnote marker node appended to the title (replacing the
 * former inline egt-pf-note). */
exports.search = function search(mark) {
  var suggestions = [ "Ember Carafe", "Order #1042", "workflow", "ateliervesper" ];
  var title = [ "Search everything." ];
  if (mark) { title.push(mark); }
  return {
    /* egt-canvas--tall == min-height:100vh (92vh mobile) + vertical-centre, the
     * framework convention (uat _egt.scss). 7.3: this section is >= a full
     * viewport, its frosted card centred; the card is fixed-height (SCSS) so
     * filtering results never reflows the section. */
    n: "section", c: "egt-section egt-canvas egt-canvas--tall egt-canvas--center egt-pf-search",
    i: [
      S.media({ img: "/imgs/pf/pf-search.jpg", alt: "", parallax: 0.08 }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-pf-search__inner",
      i: { n: "div", c: "egt-glass egt-pf-search__card egt-reveal",
      i: [
        { n: "div", c: "egt-section__head",
          i: [ { n: "p", c: "egt__eyebrow", i: "One index" },
            { n: "h2", c: "egt-section__title", i: title },
            { n: "p", c: "egt-section__lead", i: "A product, a customer, an order, a page, a message, a workflow — one index, because they're all the same kind of described thing. Type once; everything connected surfaces together." } ] },
        { n: "div", c: "egt-pf-search__panel egt-reveal", id: "egt-search",
          i: [
            { n: "div", c: "egt-pf-search__bar",
              i: [
                { n: "span", c: "egt-pf-search__glyph", "aria-hidden": "true", i: "⌕" },
                { n: "input", c: "egt-pf-search__input", id: "egt-search-input", type: "text",
                  role: "combobox", autocomplete: "off", autocapitalize: "off", spellcheck: "false",
                  "aria-expanded": "false", "aria-controls": "egt-search-list", "aria-autocomplete": "list",
                  "aria-label": "Search everything", placeholder: "Search everything…" },
                { n: "span", c: "egt-pf-search__kbd", "aria-hidden": "true", i: "/" }
              ] },
            { n: "div", c: "egt-pf-search__suggest",
              i: [ { n: "span", c: "egt-pf-search__suggest-lbl", i: "Try" } ].concat(
                suggestions.map(function (q) {
                  return { n: "button", c: "egt-pf-search__sug", type: "button", "data-search-q": q, i: q };
                })) },
            { n: "ul", c: "egt-pf-search__list", id: "egt-search-list", role: "listbox",
              "aria-label": "Results across your business",
              i: SEARCH_ROWS.map(searchRow) },
            { n: "p", c: "egt-pf-search__empty", id: "egt-search-empty", hidden: "hidden",
              i: "Nothing matches yet — try a product, a customer, or an order." }
          ] }
      ] } }
    ]
  };
};

/* =====================================================================
 * LIVE DASHBOARD PREVIEW, IN A FRAME — a server-rendered, on-brand sample
 * dashboard (stat widgets + a mini chart + a recent-orders list) inside a
 * browser/device frame. Desktop / Tablet / Phone tabs RE-FLOW the SAME
 * dashboard (one markup, CSS-driven layout via data-frame), not three
 * screenshots. Frosted frame, never a dark overlay. CSS + tiny JS
 * (initFrame), reduced-motion-safe. All sample/illustrative — no live call.
 * ===================================================================== */
var FRAME_VIEWS = [
  { id: "desktop", label: "Desktop" },
  { id: "tablet",  label: "Tablet" },
  { id: "phone",   label: "Phone" }
];

/* thousands-separator for the sample numbers (server-side, so the no-JS /
 * pre-tween value is already correctly formatted, e.g. 146760 → "146,760"). */
function dashGroup(n) { return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ","); }

/* the sample stat widgets — label, big number (tabular), tiny delta. The
 * "Atelier Vesper" sample world, shared with the Lens / Search for cohesion.
 * Revenue / Orders / Visitors / Subscribers carry a numeric `num` (tripled from
 * the original founder set) so the client can COUNT them up from 0 on view /
 * device change; Conversion (a rate, not a volume) stays a static string. */
var DASH_STATS = [
  { k: "Revenue",     num: 146760, pre: "$", d: "+12.4%", dir: "up",   sr: "up 12.4 percent" },
  { k: "Orders",      num: 3852,              d: "+8.1%",  dir: "up",   sr: "up 8.1 percent" },
  { k: "Visitors",    num: 112536,            d: "+4.6%",  dir: "up",   sr: "up 4.6 percent" },
  { k: "Conversion",  v: "3.42%",             d: "−0.3%",  dir: "down", sr: "down 0.3 percent" },
  { k: "Subscribers", num: 6420,              d: "+5.9%",  dir: "up",   sr: "up 5.9 percent" }
];

/* mini bar-chart sample (decorative; heights are % of the plot box) */
var DASH_BARS = [ 38, 52, 44, 61, 49, 70, 58, 76, 64, 82, 71, 90 ];

/* recent-orders list rows — item, amount, status pill (sample) */
var DASH_ROWS = [
  { id: "#1042", item: "Ember Carafe",   amt: "$68",  st: "Paid",    cls: "paid" },
  { id: "#1048", item: "Nightglass set", amt: "$120", st: "Packing", cls: "pack" },
  { id: "#1051", item: "Atelier Tote",   amt: "$38",  st: "Ready",   cls: "ready" },
  { id: "#1053", item: "Vesper Candle",  amt: "$30",  st: "Paid",    cls: "paid" }
];

/* the dashboard mockup, built ONCE. CSS re-lays-it-out per device tab via the
 * frame stage's data-frame attribute (desktop|tablet|phone) — same markup,
 * different layout. role="img" + aria-label so AT announces it as one
 * illustrative figure (the numbers are decorative sample data). */
function dashboardMock() {
  return {
    n: "div", c: "egt-pf-dash", role: "img",
    "aria-label": "Sample business dashboard with revenue, orders and visitor stats, a revenue chart and recent orders — illustrative data.",
    i: [
      /* a slim top-bar: brand + a couple of non-interactive nav labels */
      { n: "div", c: "egt-pf-dash__bar", "aria-hidden": "true",
        i: [
          { n: "span", c: "egt-pf-dash__brand",
            i: [ { n: "span", c: "egt-pf-dash__brand-mk", i: "A" },
              { n: "span", c: "egt-pf-dash__brand-tx", i: "Atelier Vesper" } ] },
          { n: "nav", c: "egt-pf-dash__nav",
            i: [ { n: "span", c: "egt-pf-dash__nav-i is-on", i: "Overview" },
              { n: "span", c: "egt-pf-dash__nav-i", i: "Orders" },
              { n: "span", c: "egt-pf-dash__nav-i", i: "Customers" } ] }
        ] },

      /* the dashboard body */
      { n: "div", c: "egt-pf-dash__body", "aria-hidden": "true",
        i: [
          /* 5 stat widgets — label, big number, tiny up/down delta */
          { n: "div", c: "egt-pf-dash__stats",
            i: DASH_STATS.map(function (s) {
              var val;
              if (typeof s.num === "number") {
                /* the final, correctly-formatted value renders server-side (no-JS
                 * safe); the client tweens 0 → num on view/device change. */
                var pre = s.pre || "";
                val = { n: "span", c: "egt-pf-dash__stat-v",
                  "data-count-to": s.num, "data-count-group": "1",
                  i: pre + dashGroup(s.num) };
                if (pre) { val["data-count-prefix"] = pre; }
              } else {
                val = { n: "span", c: "egt-pf-dash__stat-v", i: s.v };
              }
              return { n: "div", c: "egt-pf-dash__stat",
                i: [
                  { n: "span", c: "egt-pf-dash__stat-k", i: s.k },
                  val,
                  { n: "span", c: "egt-pf-dash__stat-d egt-pf-dash__stat-d--" + s.dir,
                    i: [ { n: "span", c: "egt-pf-dash__stat-arrow", i: s.dir === "up" ? "▲" : "▼" }, s.d ] }
                ] };
            }) },

          /* chart + list — side by side on desktop, stacked on tablet/phone */
          { n: "div", c: "egt-pf-dash__split",
            i: [
              /* mini bar chart (inline SVG, currentColor/gold, decorative) */
              { n: "div", c: "egt-pf-dash__panel egt-pf-dash__panel--chart",
                i: [
                  { n: "div", c: "egt-pf-dash__panel-hd",
                    i: [ { n: "span", c: "egt-pf-dash__panel-t", i: "Revenue" },
                      { n: "span", c: "egt-pf-dash__panel-sub", i: "Last 12 weeks" } ] },
                  { n: "div", c: "egt-pf-dash__chart",
                    i: { n: "svg", c: "egt-pf-dash__chart-svg", viewBox: "0 0 240 96",
                      preserveAspectRatio: "none", focusable: "false",
                      i: DASH_BARS.map(function (h, n) {
                        var bw = 240 / DASH_BARS.length;
                        var x = n * bw + bw * 0.22;
                        var w = bw * 0.56;
                        var bh = 96 * (h / 100);
                        /* --egt-i staggers the on-view bar grow-in (WP-N6.1);
                         * see .egt-pf-frame__stage.is-charting in the SCSS. */
                        return { n: "rect", c: "egt-pf-dash__bar" + (n === DASH_BARS.length - 1 ? " is-peak" : ""),
                          style: "--egt-i:" + n,
                          x: x.toFixed(2), y: (96 - bh).toFixed(2), width: w.toFixed(2),
                          height: bh.toFixed(2), rx: "1.5" };
                      }) } }
                ] },

              /* recent-orders list with status pills */
              { n: "div", c: "egt-pf-dash__panel egt-pf-dash__panel--list",
                i: [
                  { n: "div", c: "egt-pf-dash__panel-hd",
                    i: [ { n: "span", c: "egt-pf-dash__panel-t", i: "Recent orders" },
                      { n: "span", c: "egt-pf-dash__panel-sub", i: "Today" } ] },
                  { n: "ul", c: "egt-pf-dash__rows",
                    i: DASH_ROWS.map(function (r) {
                      return { n: "li", c: "egt-pf-dash__row",
                        i: [
                          { n: "span", c: "egt-pf-dash__row-id", i: r.id },
                          { n: "span", c: "egt-pf-dash__row-item", i: r.item },
                          { n: "span", c: "egt-pf-dash__row-amt", i: r.amt },
                          { n: "span", c: "egt-pf-dash__pill egt-pf-dash__pill--" + r.cls,
                            i: [ { n: "span", c: "egt-pf-dash__pill-dot" }, r.st ] }
                        ] };
                    }) }
                ] }
            ] }
        ] }
    ]
  };
}

/* device glyphs for the frame tabs (founder: monitor / plain tablet / phone) */
function frameIcon(id) {
  var base = { n: "svg", c: "egt-pf-frame__svg", viewBox: "0 0 24 24", fill: "none",
    stroke: "currentColor", "stroke-width": "1.8", "stroke-linecap": "round",
    "stroke-linejoin": "round", "aria-hidden": "true", focusable: "false" };
  if (id === "tablet") {
    /* a large rounded-rect screen — no camera punch-hole, a home hint */
    base.i = [ { n: "rect", x: "5", y: "3", width: "14", height: "18", rx: "2" },
      { n: "path", d: "M11 18h2" } ];
  } else if (id === "phone") {
    /* a narrow portrait rounded rectangle, speaker + home hints */
    base.i = [ { n: "rect", x: "7", y: "2", width: "10", height: "20", rx: "2.5" },
      { n: "path", d: "M10 5h4" }, { n: "path", d: "M11 18h2" } ];
  } else {
    /* a monitor: screen + stand */
    base.i = [ { n: "rect", x: "3", y: "4", width: "18", height: "12", rx: "1.5" },
      { n: "path", d: "M12 16v4" }, { n: "path", d: "M9 20h6" } ];
  }
  return base;
}

/* `mark` (optional): footnote marker node appended to the title (replacing the
 * former inline egt-pf-note). */
exports.productFrame = function productFrame(mark) {
  var title = [ "This thing moves." ];
  if (mark) { title.push(mark); }
  return {
    n: "section", c: "egt-section egt-pf-frame",
    /* WP-N6.1: a TWO-COLUMN layout — the section copy on the LEFT, the live
     * device demo on the RIGHT (stacks on mobile). The demo TOP-ALIGNS in its
     * column (see .egt-pf-frame__window margin in the SCSS) so it's visible on
     * scroll instead of buried in the middle of a tall centered container. */
    i: { n: "div", c: "egt__shell",
      i: { n: "div", c: "egt-pf-frame__layout",
      i: [
        { n: "div", c: "egt-section__head egt-reveal egt-pf-frame__head",
          i: [ { n: "p", c: "egt__eyebrow", i: "" },
            { n: "h2", c: "egt-section__title", i: title },
            { n: "p", c: "egt-section__lead", i: "The same system, designed for every screen — brand-aware, fast, and built to load before they blink. One presence that follows your customer from desk to pocket." } ] },
        /* data-frame on the stage drives the dashboard's per-device layout;
         * one tabpanel holds the SINGLE dashboard, re-flowed by CSS — not three
         * screenshots. Defaults to "desktop" (matches the first selected tab). */
        { n: "div", c: "egt-pf-frame__stage egt-reveal", id: "egt-frame", "data-frame": "desktop",
          i: [
            { n: "div", c: "egt-pf-frame__tabs", role: "tablist", "aria-label": "Choose a screen size",
              i: FRAME_VIEWS.map(function (v, i) {
                return { n: "button", c: "egt-pf-frame__tab" + (i === 0 ? " is-on" : ""), type: "button",
                  role: "tab", id: "egt-frame-tab-" + v.id, "aria-controls": "egt-frame-window",
                  "aria-selected": i === 0 ? "true" : "false", tabindex: i === 0 ? "0" : "-1",
                  "data-frame": v.id,
                  i: [ { n: "span", c: "egt-pf-frame__tab-ico", "aria-hidden": "true", i: frameIcon(v.id) },
                    { n: "span", c: "egt-pf-frame__tab-lbl", i: v.label } ] };
              }) },
            /* the browser/device chrome + window. The window is a single live
             * tabpanel re-labelled by JS as the active device tab changes. */
            { n: "div", c: "egt-pf-frame__window", id: "egt-frame-window", role: "tabpanel",
              "aria-labelledby": "egt-frame-tab-desktop", "aria-live": "polite",
              i: [
                /* DESKTOP chrome — browser traffic-lights + address bar. Hidden
                 * on tablet/phone (CSS) as the shell morphs to bare device bezel. */
                { n: "div", c: "egt-pf-frame__chrome", "aria-hidden": "true",
                  i: [ { n: "span", c: "egt-pf-frame__dot" }, { n: "span", c: "egt-pf-frame__dot" }, { n: "span", c: "egt-pf-frame__dot" },
                    { n: "span", c: "egt-pf-frame__addr", i: "ateliervesper.studio/dashboard" } ] },
                /* PHONE notch (pill + camera) / TABLET camera dot — CSS shows the
                 * right hardware per device. Pure chrome, undimmed content behind. */
                { n: "div", c: "egt-pf-frame__notch", "aria-hidden": "true",
                  i: [ { n: "span", c: "egt-pf-frame__cam" } ] },
                { n: "div", c: "egt-pf-frame__viewport", i: dashboardMock() },
                /* home indicator — shown on tablet/phone only (CSS). */
                { n: "div", c: "egt-pf-frame__home", "aria-hidden": "true" }
              ] }
          ] }
      ] } }
  };
};

/* =====================================================================
 * BENTO — "what's inside" breadth-at-a-glance. Asymmetric grid grounded
 * in real capabilities (§3): WHAT + WHY only, present-tense breadth.
 * No HOW. A few large tiles + smaller tiles convey scale without slop.
 * ===================================================================== */
/* The bento. Each tile is tagged with the DASHBOARD AREA it lives in (founder:
 * "what part of the dashboards this is in") — the platform's primary categories
 * (Build · Design · Reach · Hustle, per agents.md) plus Engage. No Live/Coming
 * hedging; everything is present-tense breadth. (Google Shopping feed removed.) */
var BENTO = [
  { size: "lg", cat: "Build", k: "Fields — the descriptive engine",
    v: "One language describes everything — pages, dashboards, checkouts, emails. Build a new thing by describing it, not by wiring a new app." },
  { size: "lg", cat: "Hustle", k: "Marketplace — Universal Checkout",
    v: "One checkout for products, variants, carts and receipts — your customer stays yours, end to end." },
  { size: "sm", cat: "Build", k: "Passwordless",
    v: "Passkey and email-code sign-in — frictionless for small business to enterprise, with HIPAA-compliant nodes available." },
  { size: "sm", cat: "Reach", k: "Your customers",
    v: "Email marketing becomes rich subscriptions — free or paid — and you can always walk away with your list." },
  { size: "sm", cat: "Build", k: "Dashboards",
    v: "List, search, detail — role-gated and built for scale, so every team runs the same source of truth." },
  { size: "sm", cat: "Build", k: "Wizards — digitize everything",
    v: "Gorgeous multistep forms that are magically easy to create and couple with workflows for easy automation." },
  { size: "lg", cat: "Hustle", k: "Subscribe & Go",
    v: "Recurring revenue for everything you offer — fans, gated content, physical and digital. Patreon energy, your brand." },
  { size: "sm", cat: "Build", k: "Domains",
    v: "Bring your own, or use our nameservers — your namespace, set up in minutes." },
  { size: "sm", cat: "Build", k: "Storage",
    v: "Your files, held in one place you own — with advanced sharing built for real teams." },
  { size: "sm", cat: "Hustle", k: "Manage your team",
    v: "Rich role-based access that lets your team go beyond making content to running your whole process." },
  { size: "sm", cat: "Hustle", k: "Save the paper",
    v: "Throw away the receipt printer — receipts stored with your customers, easy for you and simple for them." },
  { size: "sm", cat: "Hustle", k: "P.O.S.",
    v: "Your point of sale shouldn't be a brown box. It's wired to your unified product database, with filtering and controls that make selling friendly again." },
  { size: "sm", cat: "Hustle", k: "Fast Orders",
    v: "Kiosks, online ordering, to-go and pickup in store — the next generation of shop experiences." },
  { size: "lg", cat: "Build", k: "Workflows",
    v: "When something happens, the follow-up runs itself — quietly, in order, every time. No engineer required; build it in the editor." },
  { size: "sm", cat: "Design", k: "Epic Editors",
    v: "Create from elements, not boxes — modern, user-friendly experiences that take moments to make." },
  { size: "sm", cat: "Build", k: "Built with you — Intrepid",
    v: "Intrepid builds pages, adds products, and crafts experiences for you in a human-review dashboard — and keeps suggesting improvements after sleep cycles." },
  { size: "sm", cat: "Reach", k: "Discovery",
    v: "Paid discovery in the marketplace and on customers' home pages — pay for time, not pay-per-impression." },
  { size: "sm", cat: "Engage", k: "Notifications",
    v: "Interactive touch points right on the dashboard — the moments that matter, where the work happens." },
  { size: "sm", cat: "Build", k: "UAT & ua/json",
    v: "Universe App Tools render every surface from one JSON contract — crawlable, cached, brand-aware, fast by default." }
];

exports.bento = function bento() {
  return {
    n: "section", c: "egt-section egt-pf-bento",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "div", c: "egt-section__head egt-reveal",
          i: [ { n: "p", c: "egt__eyebrow", i: "What's inside" },
            { n: "h2", c: "egt-section__title", i: "One ecosystem to power your dreams." },
            { n: "p", c: "egt-section__lead", i: "With Studio Max, you're not paying extra for many of these features — one connected platform, with the pieces a real business actually needs already built in. Each tagged by the dashboard it lives in." } ] },
        { n: "div", c: "egt-grid egt-pf-bento__grid egt-reveal",
          i: BENTO.map(function (t, idx) {
            return { n: "article", c: "egt-pf-bento__tile egt-pf-bento__tile--" + t.size + " egt-lift egt-reveal", style: "--egt-i:" + idx,
              i: [
                { n: "span", c: "egt-pf-bento__chip egt-pf-bento__chip--" + t.cat.toLowerCase(), i: t.cat },
                { n: "h3", c: "egt-pf-bento__k", i: t.k },
                { n: "p", c: "egt-pf-bento__v", i: t.v }
              ] };
          }) }
      ] }
  };
};

/* =====================================================================
 * MARKETPLACE SECTION (jobs 6.6 / 6.7) — the FULL grid of the shared market
 * control (marketGrid + the MARKET product list defined above): real matched
 * product photos, TYPE badges (Subscription / Digital / Physical) and an
 * add-to-cart that ticks the section's cart-count pill. The "Do more."
 * Marketplace lens panel (sellView) renders a compact subset of the SAME
 * control, so both areas are one marketplace. Illustrative — no live checkout.
 * ===================================================================== */
exports.shop = function shop() {
  return S.section({
    eyebrow: "Marketplace",
    title: "One checkout for everything you sell.",
    lead: "Subscriptions, digital downloads and physical goods — one cart, one receipt, your customer yours end to end. Add a few and watch the cart tick.",
    c: "egt-pf-shop",
    body: [ marketGrid(MARKET, { note: "Subscriptions · digital · physical — one checkout." }) ]
  });
};

/* =====================================================================
 * PROOF-OF-DEPTH — a quiet, honest capability ledger (NOT a logo wall).
 * Real, true things stated plainly. WHY/WHAT only, firewalled.
 * ===================================================================== */
var DEPTH = [
  "Frictionless sign-in",
  "MCP Built in - for Agent discovery, Search, and Reach",
  "Your own email relay, with real DKIM",
  "Universal Checkout, built in",
  "Fields define everything; Elements render everything",
  "Dashboards, search, and step-form wizards",
  "Your brand, your namespace, your domain, your data",
  "Workflows that run themselves",
  "Universe App Tools v2 - Powerful Responsive Applications",
  "Brand-aware rendering, from the first pixel"
];

exports.depth = function depth() {
  return {
    n: "section", c: "egt-section egt-pf-depth",
    i: { n: "div", c: "egt__shell egt-reveal",
      i: [
        { n: "p", c: "egt__eyebrow", i: "Built on one spine" },
        { n: "h2", c: "egt-pf-depth__title", i: [ "Deep where it ", { n: "span", c: "egt__gold", i: "counts." } ] },
        { n: "ul", c: "egt-pf-depth__list",
          i: DEPTH.map(function (d) {
            return { n: "li", c: "egt-pf-depth__item",
              i: [ { n: "span", c: "egt-pf-depth__tick", "aria-hidden": "true", i: "✓" }, d ] };
          }) }
      ] }
  };
};

/* =====================================================================
 * CAPABILITY GRID — six doors (wayfinding). Per-lens line mark, "what you
 * get" spine, an explicit open affordance. No Live/Coming badges here
 * (founder's call — the doors are equals, not a status board).
 * ===================================================================== */
var DOOR_GETS = {
  site:        [ "Pages, profiles & media", "Brand-aware rendering", "Built to load fast" ],
  marketplace: [ "Catalog, variants & cart", "Universal Checkout", "Subscriptions & memberships" ],
  reach:       [ "From your own customer graph", "Your own email relay (real DKIM)", "You approve every send" ],
  engage:      [ "Opt-in, never a nag", "Transparent rewards", "Leave in one tap" ],
  appartisan:  [ "Reviewed before it ships", "Scoped, never blanket", "A door for builders" ],
  workflows:   [ "Trigger → steps runner", "Runs itself, every time", "Quiet, in the background" ]
};

/* per-lens line marks (24-box, currentColor, stroke) — tasteful, distinct,
 * no glow / generic 3-icon grid. site=window · marketplace=bag · reach=send ·
 * engage=heart · appartisan=extend-blocks · workflows=connected nodes. */
var DOOR_ICONS = {
  site:        [ { n: "rect", x: "3", y: "4", width: "18", height: "16", rx: "2" },
                 { n: "path", d: "M3 8.5h18" },
                 { n: "path", d: "M6 6.25h.01M8.5 6.25h.01" } ],
  marketplace: [ { n: "path", d: "M5 7.5h14l-1 12.5H6z" },
                 { n: "path", d: "M9 7.5a3 3 0 0 1 6 0" } ],
  reach:       [ { n: "path", d: "M21 3 2.5 10.5l7.5 2.8 2.8 7.5z" },
                 { n: "path", d: "M21 3 10 13.3" } ],
  engage:      [ { n: "path", d: "M12 20.5S4.5 15.9 4.5 10.9A3.6 3.6 0 0 1 12 8.1a3.6 3.6 0 0 1 7.5 2.8c0 5-7.5 9.6-7.5 9.6z" } ],
  appartisan:  [ { n: "rect", x: "3", y: "3", width: "7", height: "7", rx: "1.5" },
                 { n: "rect", x: "3", y: "14", width: "7", height: "7", rx: "1.5" },
                 { n: "rect", x: "14", y: "14", width: "7", height: "7", rx: "1.5" },
                 { n: "path", d: "M17.5 3.5v6M14.5 6.5h6" } ],
  workflows:   [ { n: "circle", cx: "6", cy: "6", r: "2.4" },
                 { n: "circle", cx: "6", cy: "18", r: "2.4" },
                 { n: "circle", cx: "18", cy: "12", r: "2.4" },
                 { n: "path", d: "M8.4 6.6 15.6 11M8.4 17.4 15.6 13" } ]
};
function doorSvg(cls, paths) {
  return { n: "svg", c: cls, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor",
    "stroke-width": "1.6", "stroke-linecap": "round", "stroke-linejoin": "round",
    "aria-hidden": "true", focusable: "false", i: paths };
}

exports.capabilityGrid = function capabilityGrid() {
  return {
    n: "div", c: "egt-grid egt-pf-doors egt-reveal",
    i: LENSES.map(function (l, idx) {
      var gets = DOOR_GETS[l.id] || [];
      var num = (idx + 1 < 10 ? "0" : "") + (idx + 1);
      return {
        n: "a", c: "egt-feat egt-pf-door egt-lift egt-reveal", href: l.href, style: "--egt-i:" + idx,
        i: [
          { n: "span", c: "egt-pf-door__accent", "aria-hidden": "true" },
          { n: "div", c: "egt-pf-door__head",
            i: [ { n: "span", c: "egt-pf-door__mark", "aria-hidden": "true",
                   i: doorSvg("egt-pf-door__ico", DOOR_ICONS[l.id] || []) },
              { n: "span", c: "egt-pf-door__idx", "aria-hidden": "true", i: num } ] },
          { n: "h3", c: "egt-feat__title egt-pf-door__title", i: l.label },
          { n: "p", c: "egt-feat__copy egt-pf-door__copy", i: l.copy },
          { n: "ul", c: "egt-pf-door__gets",
            i: gets.map(function (g) {
              return { n: "li", c: "egt-pf-door__get",
                i: [ doorSvg("egt-pf-door__check", [ { n: "path", d: "M4 12.5 9 17.5 20 6.5" } ]), g ] };
            }) },
          { n: "span", c: "egt-pf-door__go",
            i: [ { n: "span", c: "egt-pf-door__go-label", i: "Open " + l.label },
              doorSvg("egt-pf-door__go-arrow", [ { n: "path", d: "M4 12h15M13 6l6 6-6 6" } ]) ] }
        ]
      };
    })
  };
};

/* =====================================================================
 * CAPABILITY PAGE — reusable scaffold for each /platform/<seg> page.
 * One hero, the capability's REAL "See it work" demo (VIEWS[id]), an honest
 * copy spine, cross-links, and a reserve crescendo. Each page passes its own
 * art + copy.
 * ===================================================================== */

/* =====================================================================
 * SEE IT WORK — the capability's REAL interactive demo (the same lens view
 * shown on /platform) as a prominent, framed centerpiece on its own sub-page.
 * Reuses VIEWS[id]() + the polished .egt-lens-card styling, but staged large,
 * centered, and armed on load (see initStandaloneDemos in egt.platform.js) so
 * the sub-page reads as that capability's own micro-site — not a teaser strip.
 * Illustrative sample data only (IP firewall: WHAT + WHY, never HOW).
 * ===================================================================== */
function capabilityDemo(cfg, lens) {
  var view = VIEWS[cfg.id];
  if (!view) { return { n: "div" }; }
  var eyebrow = cfg.demoEyebrow || "See it work";
  var title = cfg.demoTitle || [ "Go ahead — ", { n: "span", c: "egt__gold", i: "drive it." } ];
  var lead = cfg.demoLead || lens.copy
    || "The same demo we run on the platform — interactive, on sample data.";
  var note = cfg.demoNote || "An interactive demo running on invented sample data — nothing here is a real account, and nothing is saved.";

  return {
    n: "section", c: "egt-section egt-pf-demo",
    i: { n: "div", c: "egt__shell",
      i: [
        { n: "div", c: "egt-section__head egt-pf-demo__head egt-reveal",
          i: [ { n: "p", c: "egt__eyebrow", i: eyebrow },
            { n: "h2", c: "egt-section__title", i: title },
            { n: "p", c: "egt-section__lead", i: lead } ] },
        { n: "div", c: "egt-pf-demo__stage egt-reveal",
          i: { n: "div", c: "egt-pf-demo__frame",
            i: { n: "div", c: "egt-lens-card egt-lens-card--" + cfg.id + " egt-pf-demo__card",
              i: view() } } },
        { n: "p", c: "egt-pf-demo__note egt-reveal", i: note }
      ] }
  };
}

/* =====================================================================
 * CAPABILITY RESERVE CRESCENDO — the full-viewport closing CTA, matched to
 * the /platform "Founding reservation" (.egt-pf-reserve, WP-N6.7): a real
 * crescendo, not an afterthought strip. Bookends the hero over THIS
 * capability's own media, one big frosted card, scarce gold, honest posture
 * (no dates). CTA is a plain link to the reserve page.
 * ===================================================================== */
function capabilityReserve(cfg, reserve, lens) {
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center egt-canvas--tall egt-pf-reserve",
    i: [
      S.media(cfg.reserveMedia || cfg.media || { img: "/imgs/egt-platform-canvas.jpg", alt: "", parallax: 0.05 }),
      { n: "div", c: "egt__shell egt-canvas__inner",
        i: S.glass({ width: "wide", c: "egt-pf-reserve__card egt-reveal", content: [
          { n: "p", c: "egt__eyebrow", i: "Founding reservation" },
          { n: "h2", c: "egt-pf-reserve__title", i: [ "Claim your ", { n: "span", c: "egt__gold", i: "founding place." } ] },
          { n: "p", c: "egt-pf-reserve__lead", i: "Join the public Beta for $9.57 — a non-refundable reservation on your founding spot, credited in full toward your launch, and provisioned first." },
          { n: "ul", c: "egt-pf-reserve__points", "aria-label": "What your reservation holds",
            i: [
              { n: "li", c: "egt-pf-reserve__point", i: [ { n: "span", c: "egt-pf-reserve__point-k", i: "Held for you" }, "Your founding place, kept the moment you reserve." ] },
              { n: "li", c: "egt-pf-reserve__point", i: [ { n: "span", c: "egt-pf-reserve__point-k", i: "Non-refundable, credited" }, "The full $9.57 applies to your launch — nothing lost." ] },
              { n: "li", c: "egt-pf-reserve__point", i: [ { n: "span", c: "egt-pf-reserve__point-k", i: "First in line" }, "Provisioned ahead of everyone else." ] }
            ] },
          { n: "div", c: "egt-phero__actions egt-pf-reserve__actions", i: S.cta(reserve, "Reserve your spot", true) },
          { n: "p", c: "egt-pf-reserve__fine", i: "Your founding place, held for you." }
        ] }) }
    ]
  };
}

/* a uniform accent glyph for the lens rows below — the six lenses don't each
 * have their own icon set (unlike the homepage's five rooms), and building
 * one is out of scope here; a single shared mark keeps every row honest
 * about that instead of faking distinct icons. */
function lensGlyph() {
  return { n: "svg", c: "egt-launch__mono", viewBox: "0 0 48 48", "aria-hidden": "true", focusable: "false",
    i: [
      { n: "circle", cx: "24", cy: "24", r: "3.2", fill: "currentColor", stroke: "none" },
      { n: "ellipse", cx: "24", cy: "24", rx: "17", ry: "7.5", fill: "none", stroke: "currentColor", "stroke-width": "1.5" },
      { n: "ellipse", cx: "24", cy: "24", rx: "7.5", ry: "17", fill: "none", stroke: "currentColor", "stroke-width": "1.5" }
    ] };
}

/* the "Everything belongs together" honesty section — the SAME dashboard
 * list/card panel + FLIP toggle + one-time intro demo as the homepage's
 * Studios section (reuses .egt-launch wholesale; initViewToggle() in
 * /src/egt.home.js drives it, already loaded on every platform page). Rows
 * are the six lenses; each thumbnail is that lens's own existing hero image. */
/* The launcher's FIRST row on every capability sub-page: back to the platform
 * overview (founder ask). Distinct --home styling; this is the "Explore the
 * entire platform" / return-to-/platform entry so the six-capability list/card
 * launcher reads as real microsite navigation, not just a related-links strip. */
function platformHomeRow() {
  return {
    n: "a", href: "/platform",
    c: "egt-launch__row egt-launch__row--home egt-reveal egt-reveal--up",
    style: "--egt-i:0",
    "data-room": "0",
    "aria-label": "Explore the entire platform — the whole platform, in one place.",
    i: [
      { n: "span", c: "egt-launch__thumb", "aria-hidden": "true",
        i: { n: "img", c: "egt-launch__thumb-img", src: "/imgs/egt-platform-canvas.jpg", alt: "", loading: "lazy", decoding: "async" } },
      { n: "span", c: "egt-launch__icon", "aria-hidden": "true",
        i: { n: "svg", c: "egt-launch__home-glyph", viewBox: "0 0 24 24", fill: "none",
          stroke: "currentColor", "stroke-width": "1.6", "stroke-linejoin": "round", "stroke-linecap": "round",
          i: [ { n: "path", d: "M3 10.5 12 4l9 6.5" }, { n: "path", d: "M5 9.5V20h14V9.5" } ] } },
      { n: "span", c: "egt-launch__id",
        i: [
          { n: "span", c: "egt-launch__name egt-underline", i: "Explore the entire platform" },
          { n: "span", c: "egt-launch__sub", i: "The whole platform, in one place — start here." }
        ] },
      { n: "span", c: "egt-launch__go", "aria-hidden": "true", i: "›" }
    ]
  };
}

function honestyPanel(cfg) {
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center egt-pf-honesty",
    i: [
      { n: "div", c: "egt-canvas__media", "aria-hidden": "true",
        i: { n: "img", c: "egt-canvas__img", src: (cfg.honestyMedia && cfg.honestyMedia.img) || "/imgs/egt-platform-canvas.jpg",
          alt: "", loading: "lazy", decoding: "async" } },
      { n: "div", c: "egt-canvas__inner egt-pf-honesty__inner",
        i: { n: "div", c: "egt-glass egt-launch egt-reveal egt-lift", "data-egt-tilt": "true",
          "data-egt-view": "list", "data-egt-view-root": "true", "data-egt-view-intro": "true",
          i: [
            { n: "div", c: "egt-launch__bar",
              i: [
                { n: "span", c: "egt-launch__lights", "aria-hidden": "true",
                  i: [
                    { n: "span", c: "egt-launch__dot egt-launch__dot--r" },
                    { n: "span", c: "egt-launch__dot egt-launch__dot--y" },
                    { n: "span", c: "egt-launch__dot egt-launch__dot--g" }
                  ] },
                { n: "span", c: "egt-launch__barside",
                  i: [ { n: "span", c: "egt-launch__title", i: cfg.honesty }, S.viewToggle("list") ] }
              ] },
            { n: "nav", c: "egt-launch__body", "aria-label": "Explore the platform", "data-egt-view-body": "true",
              i: [ platformHomeRow() ].concat(LENSES.map(function (l, idx) {
                return {
                  n: "a", href: l.href,
                  c: "egt-launch__row egt-reveal egt-reveal--up" + (l.id === cfg.id ? " egt-launch__row--current" : ""),
                  "aria-current": (l.id === cfg.id ? "page" : "false"),
                  style: "--egt-i:" + (idx + 1),
                  "data-room": String(idx + 1),
                  "aria-label": l.label + " — " + l.copy,
                  i: [
                    { n: "span", c: "egt-launch__thumb", "aria-hidden": "true",
                      i: { n: "img", c: "egt-launch__thumb-img", src: l.img, alt: "", width: l.imgW, height: l.imgH, loading: "lazy", decoding: "async" } },
                    { n: "span", c: "egt-launch__icon", "aria-hidden": "true", i: lensGlyph() },
                    { n: "span", c: "egt-launch__id",
                      i: [
                        { n: "span", c: "egt-launch__name egt-underline", i: l.label },
                        { n: "span", c: "egt-launch__sub", i: l.copy }
                      ] },
                    { n: "span", c: "egt-launch__go", "aria-hidden": "true", i: "›" }
                  ]
                };
              })).concat([
                /* Intrepid — not one of the six lenses (it's its own product,
                 * not a Universe Platform view), but the selector doubles as
                 * "everywhere you can go from here"; same bg + real Intrepid
                 * mark as its row in the homepage Studios launcher. */
                {
                  n: "a", href: "/intrepid",
                  c: "egt-launch__row egt-launch__row--intrepid egt-reveal egt-reveal--up",
                  style: "--egt-i:" + (LENSES.length + 1),
                  "data-room": String(LENSES.length + 1),
                  "aria-label": "Intrepid — An AI that learns your business and quietly carries the hard parts.",
                  i: [
                    { n: "span", c: "egt-launch__thumb", "aria-hidden": "true",
                      i: { n: "img", c: "egt-launch__thumb-img", src: "/imgs/egt-intrepid-canvas.jpg", alt: "", loading: "lazy", decoding: "async" } },
                    { n: "span", c: "egt-launch__icon", "aria-hidden": "true",
                      i: { n: "img", c: "egt-launch__img", src: "/imgs/intrepid-mark.svg", alt: "", loading: "lazy", decoding: "async" } },
                    { n: "span", c: "egt-launch__id",
                      i: [
                        { n: "span", c: "egt-launch__name egt-underline", i: "Intrepid" },
                        { n: "span", c: "egt-launch__sub", i: "An AI that learns your business and quietly carries the hard parts." }
                      ] },
                    { n: "span", c: "egt-launch__go", "aria-hidden": "true", i: "›" }
                  ]
                }
              ]) }
          ] } }
    ]
  };
}

/* the reusable capability-page builder ------------------------------- */
exports.capabilityPage = function capabilityPage(cfg) {
  var reserve = cfg.reserve || "/contact-us";
  var lens = LENSES.filter(function (l) { return l.id === cfg.id; })[0] || {};
  var sections = [];

  /* hero over this capability's media */
  sections.push(exports.hero({
    media: cfg.media,
    eyebrow: cfg.eyebrow || ("The Universe Platform · " + lens.label),
    title: cfg.title,
    lead: cfg.lead,
    cta: S.cta(reserve, "Join Beta", true),
    note: "Built into the platform · included in the Beta"
  }));

  /* the WHAT/WHY spine — three honest points */
  if (cfg.points && cfg.points.length) {
    var spine = S.section({
      eyebrow: cfg.spineEyebrow || "What it does",
      title: cfg.spineTitle || "Why it matters.",
      lead: cfg.spineLead,
      body: [ {
        n: "div", c: "egt-grid egt-pf-points egt-reveal",
        i: cfg.points.map(function (p, idx) {
          return { n: "article", c: "egt-feat egt-pf-point egt-reveal", style: "--egt-i:" + idx,
            i: [ { n: "span", c: "egt-pf-point__n", "aria-hidden": "true", i: "0" + (idx + 1) },
              { n: "h3", c: "egt-feat__title", i: p.title },
              { n: "p", c: "egt-feat__copy", i: p.copy } ] };
        })
      } ]
    });
    /* ANSWER-FIRST (SEO wave 2C-5): one plain, self-contained sentence — what
     * this pillar is, who it is for, and that it is part of the public beta —
     * as the first thing after the hero, ahead of the spine's own heading, so
     * a reader (or an answer engine) gets the definition before the pitch.
     * Hand-written per page in each element file; WHAT/WHY only. */
    if (cfg.answer) {
      spine.i.i.unshift({ n: "p", c: "egt-pf-answer egt-reveal", i: cfg.answer });
    }
    sections.push(spine);
  }

  /* the REAL interactive demo for this capability (the same lens view from
   * /platform), staged big + centered and armed on load. This replaces the
   * former small §4-C micro-vignette as the page's centerpiece. */
  sections.push(capabilityDemo(cfg, lens));

  /* WHO IT'S FOR + STRAIGHT ANSWERS (SEO wave 2C-7). Three outcomes for the
   * people this pillar serves, then a short visible Q&A built ONLY from
   * objections the site already answers elsewhere (the stack comparison, the
   * Intrepid and pillar copy). Plain visible copy — no FAQPage markup, no
   * rich-result expectation. Each page supplies its own wording. */
  if ((cfg.forWho && cfg.forWho.length) || (cfg.faq && cfg.faq.length)) {
    var who = [];
    if (cfg.forWho && cfg.forWho.length) {
      who.push({ n: "div", c: "egt-grid egt-pf-who egt-reveal",
        i: cfg.forWho.map(function (w, idx) {
          return { n: "article", c: "egt-feat egt-pf-who__item egt-reveal", style: "--egt-i:" + idx,
            i: [ { n: "h3", c: "egt-feat__title", i: w.title },
              { n: "p", c: "egt-feat__copy", i: w.copy } ] };
        }) });
    }
    if (cfg.faq && cfg.faq.length) {
      who.push({ n: "div", c: "egt-pf-faq egt-reveal",
        i: [ { n: "h3", c: "egt-pf-faq__title", i: cfg.faqTitle || "Straight answers." },
          { n: "dl", c: "egt-pf-faq__list",
            i: cfg.faq.map(function (qa) {
              return { n: "div", c: "egt-pf-faq__item",
                i: [ { n: "dt", c: "egt-pf-faq__q", i: qa[0] },
                  { n: "dd", c: "egt-pf-faq__a", i: qa[1] } ] };
            }) } ] });
    }
    sections.push(S.section({
      eyebrow: "Who it's for",
      title: cfg.forTitle || "Built for the people who run the business.",
      lead: cfg.forLead,
      c: "egt-pf-whosec",
      body: who
    }));
  }

  /* honesty / guardrail line if provided — now the same dashboard list/card
   * panel as the homepage's Studios section (same .egt-launch markup, same
   * list<->card FLIP toggle + one-time intro demo, driven by the shared
   * initViewToggle() in /src/egt.home.js, already loaded on every platform
   * page). Each row uses that lens's OWN hero image (no new stock photos) —
   * showing the six capabilities as one system is the point of "Everything
   * belongs together." */
  if (cfg.honesty) {
    sections.push(honestyPanel(cfg));
  }

  /* full-viewport reserve crescendo (matches /platform WP-N6.7) */
  sections.push(capabilityReserve(cfg, reserve, lens));

  /* Per-surface JSON-LD (SEO wave 2B-7): WebPage + BreadcrumbList, about the
   * /platform SoftwareApplication. Guarded on a known lens so a page can never
   * emit an empty name. Appended last so no section's position (or
   * :first-child styling) shifts. */
  var PILLAR_NAMES = { site: "Site & Profiles", marketplace: "Marketplace", reach: "Reach",
    engage: "Engage", appartisan: "AppArtisan", workflows: "Workflows" };
  if (lens.href && PILLAR_NAMES[cfg.id]) {
    sections.push(S.pageSchema({ path: lens.href,
      name: PILLAR_NAMES[cfg.id] + " — The Universe Platform",
      about: "/platform#app",
      crumbs: [ { name: "Platform", path: "/platform" }, { name: PILLAR_NAMES[cfg.id] } ] }));
  }

  /* Platform-family surface: opt into the "Join the Beta" nav CTA (founder
   * ruling 2026-08-08 — beta CTA is platform-only). Covers every capability
   * sub-page: engage, appartisan, site, marketplace, reach, workflows. */
  return S.page(reserve, sections, { betaCta: true, footerCaptureLabel: S.FOOTER_LABELS.platform });
};
